import type { WorkflowFormLayout } from '@app/shared/workflow-commands/workflow-form-layout';
import { isReviewRequestLookupCommand, reviewRequestLookupAuthoringReason } from './workflow-review-request-lookup-authoring-gate';
import { addDirectReviewRequest, upgradeDirectReviewRequest, isDirectReviewRequestReference } from './workflow-step-direct-review-request-authoring';
import { UiButtonDirective, UiControlDirective } from '@template-ui/directives';
import { UiPanelComponent } from '@template-ui/components';
import { CommonModule } from '@angular/common';
import { isBlockOwnedCommand } from '@app/phms/workflow-blocks/domain-block-manifests';
import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  TemplateRef,
  SimpleChanges,
  ViewChild,
  inject,
} from '@angular/core';
import { FormsModule, NgModel } from '@angular/forms';
import {
  WorkflowCaptureFormEditorComponent,
  WorkflowCaptureFormCopy,
} from './workflow-capture-form-editor.component';
import type { WorkflowCaptureForm } from '@app/shared/workflow-commands/workflow-capture-form';
import { isUnsupportedCaptureForm } from './workflow-capture-form-validation';
import { isUnsupportedFormConfiguration, workflowFormFieldIssues } from './workflow-form-fields-validation';
import { WorkflowFormFieldsEditorComponent } from './workflow-form-fields-editor.component';
import { WorkflowActionDataEditorComponent } from './workflow-action-data-editor.component';
import { supportsWorkflowProcessData } from './workflow-process-data';
import { WorkflowStatusEditorComponent } from './workflow-status-editor.component';
import type { WorkflowStatusOwnerRequest } from './workflow-status-owner-request';
import type { WorkflowFormConfiguration } from '@app/shared/workflow-commands/workflow-form-configuration';
import { isWorkflowFormVersionAvailable } from '@app/shared/workflow-commands/workflow-form-layout';
import { changeFormFieldLayout, enableFormLayout, formLayoutEnableReason } from './workflow-form-layout-upgrade';
import { setCaptureLookupSource } from './workflow-capture-lookup-authoring';
import { preparationLookupSettingsAuthoringReason, reviewRequestLookupSettingsAuthoringReason, providedFormSettingsAuthoringReason, providedFormSettingsShapeReason, providedLookupAuthoringReason, providedLookupNeedsFormatUpgrade, setProvidedLookupSource, type ProvidedFormAuthoringContext } from './workflow-provided-lookup-authoring';
import { isPreparationLookupCommand, preparationLookupAuthoringReason } from './workflow-preparation-lookup-authoring-gate';
import { addPreparation, upgradePreparation, isPreparationReference } from './workflow-step-preparation-authoring';
import type { WorkflowLookupSource, WorkflowLookupSourceOwner } from './workflow-lookup-source-authoring.models';
import { workflowCompleteCommandAdapter } from '@app/shared/workflow-commands/workflow-complete-command-adapter';
import type { WorkflowCompletionRequirement } from '@app/shared/workflow-commands/workflow-completion-requirements';
import { firstValueFrom } from 'rxjs';
import {
  WorkflowDefinitionDocument,
  WorkflowDefinitionNode,
  requireWorkflowPayloadVersion,
} from './platform-workflow-definition-lifecycle.models';
import { PlatformWorkflowDefinitionLifecycleService } from './platform-workflow-definition-lifecycle.service';
import {
  capabilityIdentity,
  sameCapabilityReference,
  WorkflowCapabilityCatalogEntry,
} from './workflow-capability-authoring';
import {
  hasEditableRouting,
  replacePresentation,
} from './workflow-presentation-authoring';
import {
  directRoutePresentation,
  routeDestinationPresentation,
} from './workflow-route-presentation';
import {
  addStepCommand,
  addStepView,
  addStepDashboard,
  applyComponentRoutes,
  commandIdentity,
  eligibleStepDestinations,
  initializeBlankStep,
  nearestPreviousWorkSteps,
  readStepOutcomeRoutes,
  setStepNextPath,
  StepAuthoringCatalog,
  StepAuthoringCommand,
  StepCommand,
} from './workflow-step-composition';
import { addReviewDecision as prepareReviewDecision, upgradeReviewDecision } from './workflow-step-review-authoring';
import {
  applyStepOutcomeStatuses,
  readStepOutcomeStatus,
  stepOutcomeStatusNotice,
} from './workflow-step-outcome-status';
import {
  emptyStatusConfiguration,
  replaceStatusConfiguration,
} from './workflow-status-authoring';
import {
  WorkflowOwnerViewState,
  WorkflowOwnerFocus,
  captureWorkflowOwnerFocus,
  restoreWorkflowOwnerFocus,
} from './workflow-owner-view-state';
import { WorkflowEditorPendingScope, WorkflowEditorBufferState } from './workflow-editor-pending-state';
import { WorkflowLibrarySelectorComponent } from './workflow-library-selector.component';
import { WorkflowReviewSetupDialogComponent, type WorkflowReviewSetupDialogModel } from './workflow-review-setup-dialog.component';
import { readWorkflowReviewSetupSummary } from './workflow-review-setup-summary';
import { WorkflowLibraryCategory, WorkflowLibraryClosedReason, WorkflowLibraryEntry, WorkflowLibraryFooterAction } from './workflow-library-selector.types';
import { WorkflowWorkFormEditorComponent } from './workflow-work-form-editor.component';

type WorkOwnerSelection = {
  kind: 'responsibility' | 'form' | 'action' | 'outcomes' | 'library' | 'next';
  commandId?: string;
  outcomeCode?: string;
};

function sameSavedRouteValue(left: unknown, right: unknown): boolean {
  if (Object.is(left, right)) return true;
  if (!left || !right || typeof left !== 'object' || typeof right !== 'object') return false;
  if (Array.isArray(left) || Array.isArray(right)) {
    if (!Array.isArray(left) || !Array.isArray(right) || left.length !== right.length) return false;
  } else if (![Object.prototype, null].includes(Object.getPrototypeOf(left)) || ![Object.prototype, null].includes(Object.getPrototypeOf(right))) return false;
  const keys = Object.keys(left), otherKeys = Object.keys(right);
  return keys.length === otherKeys.length && keys.every(key => Object.hasOwn(right, key) &&
    sameSavedRouteValue((left as Record<string, unknown>)[key], (right as Record<string, unknown>)[key]));
}

@Component({
  selector: 'phms-workflow-step-components-editor',
  standalone: true,
  imports: [
    UiButtonDirective,
    UiControlDirective,
    UiPanelComponent,
    WorkflowLibrarySelectorComponent,
    WorkflowReviewSetupDialogComponent,
    WorkflowWorkFormEditorComponent,
    CommonModule,
    FormsModule,
    WorkflowFormFieldsEditorComponent,
    WorkflowCaptureFormEditorComponent,
    WorkflowActionDataEditorComponent,
    WorkflowStatusEditorComponent,
  ],
  templateUrl: './workflow-step-components-editor.component.html',
  styleUrl: './workflow-step-components-editor.component.scss',
})
export class WorkflowStepComponentsEditorComponent
  implements OnChanges, OnDestroy
{
  readonly supportsProcessData = supportsWorkflowProcessData;
  private readonly element = inject<ElementRef<HTMLElement>>(ElementRef);
  @Input({ required: true }) document!: WorkflowDefinitionDocument;
  @Input({ required: true }) node!: WorkflowDefinitionNode;
  @Input() disabled = false;
  @Input() active = true;
  /** The Work overview summarizes this owner's existing partition without rendering another editor. */
  @Input() overview = false;
  @Input() contextualWorkHeader = false;
  @Input() latestDocument: (() => WorkflowDefinitionDocument | null) | null = null;
  @Input() ownerContext: (() => unknown) | null = null;
  /** Presentation ownership survives a compatible saved revision; mutation guards still use ownerContext. */
  @Input() formViewContext: (() => unknown) | null = null;
  @Input() formLookupOwner: (() => WorkflowLookupSourceOwner | null) | null = null;
  readonly currentFormLookupOwner = () => this.formLookupOwner?.() ?? null;
  /** Retained forms can activate without a new declaration; availability stays live. */
  readonly currentFormEditContext = () => JSON.stringify([this.ownerKey(), this.scope, this.selection, this.node.id]);
  readonly currentProvidedFormAuthoringContext = (): ProvidedFormAuthoringContext | null => {
    const document = this.currentDocument();
    if (!document || this.destroyed || !this.ownerIsCurrent() || this.formSetup || !this.selection.commandId) return null;
    const nodes = document.nodes.filter(node => node.id === this.node.id);
    const commands = nodes.length === 1 ? nodes[0].declaration?.behavior.commands
      .filter(command => commandIdentity(command) === this.selection.commandId) ?? [] : [];
    const installed = commands.length === 1 ? this.catalog?.commands.filter(contract => commandIdentity(contract) === commandIdentity(commands[0])) ?? [] : [];
    return commands.length === 1 && installed.length === 1 && sameCapabilityReference(installed[0].capability, commands[0].capability) ? { ownerKey: this.currentFormEditContext(), document,
      nodeId: this.node.id, command: commands[0], installed: installed[0], catalog: this.catalog ?? undefined } : null;
  };
  readonly currentFormEditAvailable = () => this.active && !this.overview && !this.disabled && !this.loading
    && !this.formActionSettings && !this.workEditingUnavailable && !this.formSetup;
  readonly currentFormLookupContext = () => JSON.stringify([this.ownerKey(), this.currentDocument(), this.scope, this.selection,
    this.node.id, this.active, this.overview, this.disabled, this.loading, this.formActionSettings, !!this.formSetup]);
  @Output() workSectionRequested = new EventEmitter<'form' | 'action'>();
  @Output() workOverviewRequested = new EventEmitter<void>();
  @Output() openRequirements = new EventEmitter<void>();
  @Output() openStaffPage = new EventEmitter<void>();
  @Input() scope: 'behavior' | 'dashboard' | 'detail' | 'work' = 'behavior';
  @Input() resetVersion = 0;
  @Input() hideDashboardPicker = false;
  @Input() hideDetailEditor = false;
  @Input() inspectorOnly = false;
  /** Complete, saved routes can be changed without a second Apply action. */
  @Input() immediateRoutingChanges = false;
  @Input() externalWorkflowStatuses = false;
  @Input() workflowStatusesView: (() => string | null) | null = null;
  @Input() actionIdentity = '';
  /** Undefined loads here; null waits for the owning editor's catalog. */
  @Input() componentCatalog: StepAuthoringCatalog | null | undefined =
    undefined;
  @Input() componentCapabilities: WorkflowCapabilityCatalogEntry[] = [];
  @Input() focusAction: {
    capability: StepCommand['capability'];
    request: number;
    outcomes?: boolean;
    outcomeCode?: string;
  } | null = null;
  private lastFocusAction: typeof this.focusAction = null;
  @Input() focusForm: {
    capability: StepCommand['capability'];
    request: number;
  } | null = null;
  private lastFocusForm: typeof this.focusForm = null;
  @Input() responsibilityEditor: TemplateRef<unknown> | null = null;
  @Input() responsibilitySummary = 'Choose who receives this work';
  @Input() beforeSelectionChange: (() => boolean) | null = null;
  @Input() focusResponsibility: { request: number } | null = null;
  @Output() openForm = new EventEmitter<StepCommand>();
  @Output() openAdvanced = new EventEmitter<void>();
  @Output() openActions = new EventEmitter<StepCommand | undefined>();
  @Output() openOutcomes = new EventEmitter<StepCommand>();
  @Output() actionSelectionChange = new EventEmitter<StepCommand | null>();
  @Output() capabilitiesChange = new EventEmitter<
    WorkflowCapabilityCatalogEntry[]
  >();
  @Output() pendingChangesChange = new EventEmitter<boolean>();
  @Output() documentChange = new EventEmitter<WorkflowDefinitionDocument>();
  @Output() catalogChange = new EventEmitter<StepAuthoringCatalog>();
  @Output() openPaths = new EventEmitter<void>();
  @Output() openOutcomePath = new EventEmitter<{ command: StepCommand; outcomeCode: string }>();
  @Output() openDirectPath = new EventEmitter<void>();
  @Output() openWorkflowStatuses = new EventEmitter<string>();
  @Output() workflowStatusesOwnerRequested = new EventEmitter<WorkflowStatusOwnerRequest>();
  private externalWorkflowStatusesView: string | null = null;
  private readonly service = inject(PlatformWorkflowDefinitionLifecycleService);
  @ViewChild(WorkflowActionDataEditorComponent)
  private dataEditor?: WorkflowActionDataEditorComponent;
  @ViewChild(WorkflowStatusEditorComponent)
  private statusEditor?: WorkflowStatusEditorComponent;
  workflowStatusesOpen = false;
  private statusFocusRequest = 0;
  catalog: StepAuthoringCatalog | null = null;
  private capabilities: WorkflowCapabilityCatalogEntry[] = [];
  private context = '';
  private request = 0;
  error = '';
  catalogError = '';
  message = '';
  loading = false;
  get workEditingUnavailable(): boolean {
    return this.scope === 'work' && !hasEditableRouting(this.document);
  }
  selectedAction = '';
  selectedView = '';
  selection: WorkOwnerSelection = { kind: 'next' };
  workLibraryKind: 'form' | 'action' = 'action';
  formActionSettings = false;
  readonly initializedFormSettings = new Set<string>();
  actionLibraryOpen = false;
  readonly actionCategories: readonly WorkflowLibraryCategory[] = [{ id: 'saves', label: 'Saves work' }, { id: 'completes', label: 'Completes step' }];
  readonly actionFooterActions: readonly WorkflowLibraryFooterAction[] = [{ id: 'forms', label: 'Open Forms' }, { id: 'staff', label: 'Open Staff page' }];
  private inputOwner = '';
  private inputContext = '';
  private navigationOwner = '';
  private navigationContext = '';
  private destroyed = false;
  private actionLibrarySnapshot = '';
  private actionLibraryRelease: (() => void) | null = null;
  private actionLibraryDestination: { kind: 'add'; reference: StepCommand['capability'] } | { kind: 'requirements' | 'forms' | 'staff' } | null = null;
  formLibraryOpen = false;
  readonly formCategories: readonly WorkflowLibraryCategory[] = [
    { id: 'custom', label: 'Custom fields' }, { id: 'provided', label: 'Provided by actions' },
  ];
  readonly formFooterActions: readonly WorkflowLibraryFooterAction[] = [
    { id: 'requirements', label: 'Open requirements' }, { id: 'staff', label: 'Open Staff page' },
  ];
  private formLibrarySnapshot = '';
  private formLibraryRelease: (() => void) | null = null;
  private formLibraryDestination: { kind: 'existing' | 'add' | 'preparation' | 'direct-request'; reference: StepCommand['capability'] }
    | { kind: 'requirements' | 'actions' | 'staff' } | null = null;
  reviewLibraryOpen = false;
  reviewLibraryEntries: readonly WorkflowLibraryEntry[] = [];
  readonly reviewCategories: readonly WorkflowLibraryCategory[] = [{ id: 'review', label: 'Review forms' }];
  private reviewLibrarySnapshot = '';
  private reviewLibraryRelease: (() => void) | null = null;
  private reviewLibraryDestination: StepCommand['capability'] | null = null;
  reviewSetupDialogOpen = false;
  reviewSetupDialogError = '';
  private reviewSetupGeneration = 0;
  private reviewSetupReturnFocus: HTMLElement | null = null;
  private reviewSetupReturnKind: 'add' | 'change' = 'add';
  private reviewSetupOrigin: { buffer: object; baseline: WorkflowDefinitionDocument; owner: string; generation: number; openedContext?: string; returnFocus: HTMLElement | null; returnKind: 'add' | 'change' } | null = null;
  private reviewSetupLease: { context: string; release: () => void } | null = null;
  private reviewSetupAppliedFocus: { context: string; owner: string; document: string; commandId: string } | null = null;

  formSetup: { kind: 'preparation' | 'direct-request'; owner: string; baseline: string; catalog: string; reference: StepCommand['capability']; candidate: WorkflowDefinitionDocument } | null = null;
  formSetupError = '';
  get preparationSetup() { return this.formSetup?.kind === 'preparation' ? this.formSetup : null; }
  get directRequestSetup() { return this.formSetup?.kind === 'direct-request' ? this.formSetup : null; }
  get preparationSetupError() { return this.preparationSetup ? this.formSetupError : ''; }
  get directRequestSetupError() { return this.directRequestSetup ? this.formSetupError : ''; }
  get stagedFormName(): string { return this.formSetup?.kind === 'direct-request' ? 'Review request' : 'Preparation'; }
  get stagedFormTitle(): string { return this.formSetup?.kind === 'direct-request' ? 'Review request with lookup choices' : 'Preparation with lookup choices'; }
  get stagedFormApplyLabel(): string { return `Apply ${this.stagedFormName} form`; }
  private selectedNodeId = '';
  private selectedDocumentContext = '';
  private readonly ownerViews = inject(WorkflowOwnerViewState, { optional: true }) ?? new WorkflowOwnerViewState();
  private ownerViewKey = '';
  private ownerFocus: WorkflowOwnerFocus | null = null;
  private ownerFocusSelection = '';
  private ownerFocusRequest = 0;
  private ownerDocument: WorkflowDefinitionDocument | null = null;
  private ownerNodeId = '';
  private readonly routeOutcomeSelections = new Map<string, string>();
  private routeFocusRequest = 0;
  private restoringRouteModel = false;
  private selectedRoutePin: StepCommand['capability'] | null = null;

  @HostListener('focusin', ['$event'])
  rememberOwnerFocus(event: FocusEvent): void {
    this.ownerFocusRequest++;
    this.routeFocusRequest++;
    this.ownerFocus = captureWorkflowOwnerFocus(this.element.nativeElement, event.target);
    this.ownerFocusSelection = JSON.stringify(this.selection);
  }

  private rememberOwnerView(): void {
    if (!this.ownerViewKey) return;
    this.ownerViews.remember(this.ownerViewKey, {
      selection: this.selection,
      commandPin: this.selection.kind === 'outcomes' ? this.selectedRoutePin : this.ownerDocument?.nodes.find(node => node.id === this.ownerNodeId)?.declaration?.behavior.commands
        .find(command => commandIdentity(command) === this.selection.commandId)?.capability,
      workLibraryKind: this.workLibraryKind,
      formActionSettings: this.formActionSettings,
      routeOutcomeSelections: [...this.routeOutcomeSelections],
      focus: this.ownerFocus,
      focusSelection: this.ownerFocusSelection,
    });
  }

  private restoreOwnerView(): void {
    const retained = this.ownerViews.read<{
      selection: WorkOwnerSelection;
      commandPin?: StepCommand['capability'];
      workLibraryKind: 'form' | 'action';
      formActionSettings?: boolean;
      routeOutcomeSelections?: [string, string][];
      focus: WorkflowOwnerFocus | null;
      focusSelection: string;
    }>(this.ownerViewKey);
    if (!retained || this.inspectorOnly || this.document.nodes.filter(node => node.id === this.node.id).length !== 1) return;
    const selection = retained.selection;
    if (selection.commandId) {
      const matches = this.commands.filter(command => commandIdentity(command) === selection.commandId);
      if (matches.length !== 1 || !retained.commandPin || !sameCapabilityReference(matches[0].capability, retained.commandPin)) {
        this.formActionSettings = false;
        this.selection = this.scope === 'work' ? { kind: 'responsibility' } : { kind: 'next' };
        this.error = 'The previously selected form or action is unavailable or ambiguous. Select an item to inspect its saved settings.';
        return;
      }
    }
    // Library candidates and staged route buffers are intentionally never retained.
    if (selection.kind === 'library') return;
    this.selection = selection;
    if (selection.kind === 'outcomes') this.selectedRoutePin = retained.commandPin ?? null;
    for (const [key, code] of retained.routeOutcomeSelections ?? []) this.routeOutcomeSelections.set(key, code);
    this.workLibraryKind = retained.workLibraryKind;
    this.formActionSettings = retained.formActionSettings === true;
    if (this.formActionSettings && selection.commandId) this.initializedFormSettings.add(selection.commandId);
    this.ownerFocus = retained.focus;
    this.ownerFocusSelection = retained.focusSelection;
  }

  private restoreOwnerFocus(): void {
    const request = ++this.ownerFocusRequest;
    const key = this.ownerViewKey;
    const selection = JSON.stringify(this.selection);
    const context = () => { const document = this.currentDocument(); return JSON.stringify([this.ownerKey(), document && this.documentContext(document)]); };
    const captured = context();
    setTimeout(() => {
      const host = this.element.nativeElement;
      if (this.destroyed || captured !== context() || !host.isConnected || host.closest('[hidden],[inert]') || this.ownerViews.focusSuspended() || request !== this.ownerFocusRequest || !this.active || key !== this.ownerViewKey || selection !== JSON.stringify(this.selection)) return;
      if (selection === this.ownerFocusSelection && restoreWorkflowOwnerFocus(host, this.ownerFocus)) return;
      const heading = host.querySelector<HTMLElement>('[data-owner-focus="work-selection"]');
      if (heading?.isConnected && !heading.closest('[hidden], [inert]')) heading.focus({ preventScroll: true });
    });
  }
  private routeBuffer: {
    context: string;
    nodeType: string;
    commandId: string;
    original: string;
    originalPaths: string;
    originalWorkflowStatuses: string;
    workflowStatuses: Record<string, string>;
    originalDeclaration?: string;
    setupDocument?: WorkflowDefinitionDocument;
    setupCatalog?: string;
    immediateEligible: boolean;
    savedRouteSummaries?: Record<string, string>;
    command: StepCommand;
    returns: Set<string>;
  } | null = null;
  routeError = '';
  readonly trackOutcome = (
    _: number,
    outcome: StepCommand['outcomes'][number],
  ) => outcome.code;
  readonly trackRouteRow = (index: number) => index;
  get selectedCommand(): StepCommand | undefined {
    return this.commands.find(
      (command) => commandIdentity(command) === this.selection.commandId,
    );
  }
  get inspectorTitle(): string {
    if (this.selection.kind === 'responsibility') return 'Responsibility';
    if (this.selection.kind === 'library')
      return this.scope === 'work' && this.workLibraryKind === 'form'
        ? 'Add a form'
        : 'Add an action';
    if (this.selection.kind === 'next') return 'Next step';
    return this.selectedCommand
      ? `${this.label(this.selectedCommand)}${this.selection.kind === 'outcomes' ? ' outcomes' : ''}`
      : 'Staff action';
  }
  get workContextTitle(): string {
    return this.formActionSettings && this.selectedCommand ? `${this.label(this.selectedCommand)} · Action settings`
      : this.workLibraryKind === 'form' ? 'Forms' : 'Actions';
  }
  focusWorkContext(): void { this.restoreOwnerFocus(); }
  requestWorkOverview(): void {
    if (this.scope === 'work' && this.active && !this.overview && this.navigationIsCurrent()) this.workOverviewRequested.emit();
  }
  private ownerKey(): string { return JSON.stringify([this.ownerContext?.()]); }
  private ownerIsCurrent(): boolean { return !this.ownerContext || this.ownerKey() === this.inputOwner; }
  private navigationIsCurrent(): boolean {
    const document = this.currentDocument(), nodes = document?.nodes.filter(node => node.id === this.node.id) ?? [];
    const current = !!document && (!this.ownerContext || this.ownerKey() === this.navigationOwner) &&
      this.documentContext(document) === this.navigationContext && nodes.length === 1 && nodes[0].typeCode === this.node.typeCode;
    if (!current) this.error = 'The selected workflow or step changed. Return to its current Work view before opening another editor.';
    return current;
  }
  private currentDocument(): WorkflowDefinitionDocument | null { return this.latestDocument ? this.latestDocument() : this.document; }
  private adoptCurrentDocument(): boolean {
    const document = this.currentDocument();
    const nodes = document?.nodes.filter(node => node.id === this.node.id) ?? [];
    if (!document || this.destroyed || !this.ownerIsCurrent() || this.documentContext(document) !== this.inputContext ||
      nodes.length !== 1 || nodes[0].typeCode !== this.node.typeCode) {
      this.error = 'The workflow or work-step context changed. Current settings and pending input are retained; review this owner before editing again.';
      return false;
    }
    this.document = document; this.node = nodes[0];
    return true;
  }
  private refreshSettledSource(document = this.currentDocument()): void {
    if (!document || this.hasPendingChanges()) return;
    const nodes = document.nodes.filter(node => node.id === this.node.id);
    if (nodes.length !== 1 || nodes[0].typeCode !== this.node.typeCode) return;
    this.document = document; this.node = nodes[0];
    this.inputOwner = this.ownerKey(); this.inputContext = this.documentContext(document);
  }
  private exactCurrentCommand(command: StepCommand, document = this.document): StepCommand {
    const nodes = document.nodes.filter(node => node.id === this.node.id);
    const rows = nodes.length === 1 ? nodes[0].declaration?.behavior.commands.filter(item => commandIdentity(item) === commandIdentity(command)) ?? [] : [];
    if (rows.length !== 1 || !sameCapabilityReference(rows[0].capability, command.capability) || JSON.stringify(rows[0]) !== JSON.stringify(command))
      throw new Error('This action changed while its settings were open. Its current settings are retained; select it again before editing.');
    return rows[0];
  }
  openFormActionSettings(command: StepCommand): void {
    if (!this.active || this.overview || !this.adoptCurrentDocument() ||
      (this.beforeSelectionChange && !this.beforeSelectionChange()) || !this.applyPendingChanges()) return;
    try {
      const current = this.exactCurrentCommand(command);
      if (!this.workFormCommands.some(item => sameCapabilityReference(item.capability, current.capability))) return;
      this.selection = { kind: 'form', commandId: commandIdentity(current) }; this.workLibraryKind = 'form';
      this.formActionSettings = true; this.initializedFormSettings.add(commandIdentity(current));
      this.rememberOwnerView(); this.restoreOwnerFocus();
    } catch (error) { this.error = error instanceof Error ? error.message : 'Select this form again.'; }
  }
  backToWorkForm(): void {
    if (!this.active || (this.beforeSelectionChange && !this.beforeSelectionChange()) || !this.applyPendingChanges()) return;
    this.formActionSettings = false; this.rememberOwnerView(); this.restoreOwnerFocus();
  }
  showRequirements(): void {
    if (this.active && this.navigationIsCurrent() && (!this.beforeSelectionChange || this.beforeSelectionChange()) && this.applyPendingChanges()) this.openRequirements.emit();
  }
  get actionLibraryEntries(): readonly WorkflowLibraryEntry[] {
    return this.libraryChoices.map(choice => ({ id: JSON.stringify(choice.command.capability), label: choice.command.label,
      description: choice.command.description, categoryId: choice.command.completesStep === false ? 'saves' : 'completes',
      disabledReason: choice.reason || undefined, actionLabel: !choice.reason && choice.command.completesStep === false && this.isFormContract(choice.command) ? 'Add & edit form' : 'Add action',
      ownerAction: choice.reason && !!this.node.declaration?.behavior.completionRequirements?.length && choice.command.completesStep !== false &&
        choice.command.supportsCompletionRequirements !== true ? { id: 'requirements', label: 'View requirements' } : undefined }));
  }
  /** Inactive rendering keeps the display token; active rendering and gestures stay fresh. */
  actionLibraryRenderContext(): string {
    return this.active ? this.actionLibraryContext() : this.actionLibrarySnapshot;
  }
  actionLibraryContext(): string {
    const document = this.currentDocument();
    return JSON.stringify([this.ownerKey(), document && this.documentContext(document), document?.payloadSchemaVersion, document?.nodes, document?.edges,
      document?.capabilityReferences, document?.processData, this.catalog, this.capabilities, this.disabled, this.loading, this.catalogError, this.active, this.overview, this.scope]);
  }
  private openActionLibrary(): boolean {
    if (this.disabled || this.loading || this.workEditingUnavailable || this.catalogError || !this.active || this.overview ||
      this.formLibraryOpen || this.reviewLibraryOpen || this.ownerViews.focusSuspended() ||
      !this.adoptCurrentDocument() || (this.beforeSelectionChange && !this.beforeSelectionChange()) || !this.applyPendingChanges()) return false;
    this.workLibraryKind = 'action'; this.actionLibraryDestination = null;
    this.actionLibrarySnapshot = this.actionLibraryContext(); this.actionLibraryOpen = true;
    return true;
  }
  actionLibraryOpened(): void { this.actionLibraryRelease ??= this.ownerViews.suspendFocus(); }
  chooseActionLibrary(id: string): void {
    if (!this.actionLibraryOpen || !this.active || this.actionLibrarySnapshot !== this.actionLibraryContext() || !this.adoptCurrentDocument()) return;
    const choices = this.libraryChoices.filter(choice => JSON.stringify(choice.command.capability) === id);
    if (choices.length !== 1 || choices[0].reason || this.disabled || this.loading || this.catalogError) return;
    const exact = this.catalog?.commands.filter(command => commandIdentity(command) === commandIdentity(choices[0].command)) ?? [];
    if (exact.length !== 1 || !sameCapabilityReference(exact[0].capability, choices[0].command.capability)) return;
    this.actionLibraryDestination = { kind: 'add', reference: structuredClone(exact[0].capability) }; this.actionLibraryOpen = false;
  }
  requestActionLibraryOwner(event: { id: string; actionId: string }): void {
    if (!this.actionLibraryOpen || this.actionLibrarySnapshot !== this.actionLibraryContext() || !this.adoptCurrentDocument()) return;
    const entries = this.actionLibraryEntries.filter(entry => entry.id === event.id && entry.ownerAction?.id === event.actionId);
    if (entries.length === 1 && event.actionId === 'requirements') { this.actionLibraryDestination = { kind: 'requirements' }; this.actionLibraryOpen = false; }
  }
  requestActionLibraryFooter(id: string): void {
    if (!this.actionLibraryOpen || this.disabled || this.loading || this.catalogError ||
      this.actionLibrarySnapshot !== this.actionLibraryContext() || !this.adoptCurrentDocument()) return;
    if (id === 'forms' || id === 'staff') { this.actionLibraryDestination = { kind: id }; this.actionLibraryOpen = false; }
  }
  actionLibraryClosed(reason: WorkflowLibraryClosedReason): void {
    this.actionLibraryRelease?.(); this.actionLibraryRelease = null;
    const destination = this.actionLibraryDestination;
    this.actionLibraryDestination = null; this.actionLibraryOpen = false;
    if (this.destroyed || reason !== 'input' || !destination || !this.active || this.actionLibrarySnapshot !== this.actionLibraryContext() || !this.adoptCurrentDocument()) return;
    if (destination.kind === 'requirements') this.openRequirements.emit();
    else if (destination.kind === 'staff') this.openStaffPage.emit();
    else if (destination.kind === 'forms') this.focusWorkSection('form');
    else if (destination.kind === 'add') {
      const matches = this.catalog?.commands.filter(contract => sameCapabilityReference(contract.capability, destination.reference)) ?? [];
      if (matches.length !== 1) return;
      this.addAction(matches[0]);
      if (!this.error) this.restoreOwnerFocus();
    }
  }
  get formLibraryEntries(): readonly WorkflowLibraryEntry[] {
    const existing = this.workFormCommands.map(command => ({
      id: `existing:${commandIdentity(command)}`,
      label: `Existing: ${this.formTitle(command)}`,
      description: `${this.label(command)} · Already on this step.`,
      categoryId: command.captureForm || this.exactActionContract(command)?.supportsCaptureForm ? 'custom' : 'provided',
      actionLabel: 'Edit existing form',
      icon: 'pi pi-file',
    }));
    const choices = this.libraryChoices.filter(choice => !isPreparationLookupCommand(choice.command.capability) && !isReviewRequestLookupCommand(choice.command.capability)).map(choice => {
      const requirements = !!this.node.declaration?.behavior.completionRequirements?.length && choice.command.completesStep !== false &&
        choice.command.supportsCompletionRequirements !== true;
      const inspectActions = choice.command.exclusiveCompletion && this.commands.length > 0;
      return {
        id: JSON.stringify(choice.command.capability), label: choice.command.label,
        description: [choice.command.description, choice.command.supportsCaptureForm ? 'Custom fields.' : 'Fields are provided by the action.'].filter(Boolean).join(' '),
        categoryId: choice.command.supportsCaptureForm ? 'custom' : 'provided', icon: 'pi pi-file',
        disabledReason: choice.reason || undefined, actionLabel: 'Add form',
        ownerAction: choice.reason && (requirements || inspectActions)
          ? inspectActions ? { id: 'actions', label: 'View existing work' } : { id: 'requirements', label: 'View requirements' } : undefined,
      };
    });
    const preparation = (this.catalog?.commands ?? []).filter(contract => isPreparationLookupCommand(contract.capability)).map(contract => {
      let reason = this.node.declaration?.behavior.commands.some(command => sameCapabilityReference(command.capability, contract.capability))
        ? 'This step already uses this Preparation form.' : '';
      if (!reason) {
        try { this.preparedPreparation(contract.capability); }
        catch (error) { reason = error instanceof Error ? error.message : 'This Preparation form is unavailable for this step.'; }
      }
      return { id: `preparation:${JSON.stringify(contract.capability)}`, label: 'Preparation with lookup choices',
        description: 'Submit work for independent review. Choose a lookup source separately in Fields.',
        categoryId: 'provided', icon: 'pi pi-file', actionLabel: 'Prepare form change', disabledReason: reason || undefined };
    });
    const requests = (this.catalog?.commands ?? []).filter(contract => isReviewRequestLookupCommand(contract.capability)).map(contract => {
      let reason = this.commands.some(command => sameCapabilityReference(command.capability, contract.capability))
        ? 'This step already uses this review request form.' : '';
      if (!reason) {
        try { this.preparedDirectRequest(contract.capability); }
        catch (error) { reason = error instanceof Error ? error.message : 'This review request form is unavailable for this step.'; }
      }
      return { id: `direct-request:${JSON.stringify(contract.capability)}`, label: 'Review request with lookup choices',
        description: 'Request an independent review on this step. Choose a lookup source separately in Fields.',
        categoryId: 'provided', icon: 'pi pi-file', actionLabel: 'Prepare form change', disabledReason: reason || undefined };
    });
    return [...existing, ...choices, ...preparation, ...requests];
  }
  /** Inactive rendering keeps the display token; active rendering and gestures stay fresh. */
  formLibraryRenderContext(): string {
    return this.active ? this.formLibraryContext() : this.formLibrarySnapshot;
  }
  formLibraryContext(): string {
    return JSON.stringify([this.actionLibraryContext(), this.selection, this.workLibraryKind, this.formActionSettings]);
  }
  private openFormLibrary(): boolean {
    if (this.disabled || this.loading || this.workEditingUnavailable || this.catalogError || !this.active || this.overview ||
      this.actionLibraryOpen || this.formLibraryOpen || this.reviewLibraryOpen || this.ownerViews.focusSuspended() || !this.adoptCurrentDocument() ||
      (this.beforeSelectionChange && !this.beforeSelectionChange()) || !this.applyPendingChanges()) return false;
    this.workLibraryKind = 'form'; this.formLibraryDestination = null;
    this.formLibrarySnapshot = this.formLibraryContext(); this.formLibraryOpen = true;
    return true;
  }
  formLibraryOpened(): void { this.formLibraryRelease ??= this.ownerViews.suspendFocus(); }
  chooseFormLibrary(id: string): void {
    if (!this.formLibraryOpen || this.disabled || this.loading || this.catalogError || !this.active || this.overview ||
      this.formLibrarySnapshot !== this.formLibraryContext() || !this.adoptCurrentDocument()) return;
    const entries = this.formLibraryEntries.filter(entry => entry.id === id);
    if (entries.length !== 1 || entries[0].disabledReason) return;
    if (id.startsWith('preparation:')) {
      const matches = this.catalog?.commands.filter(contract => `preparation:${JSON.stringify(contract.capability)}` === id) ?? [];
      if (matches.length !== 1) return;
      this.formLibraryDestination = { kind: 'preparation', reference: structuredClone(matches[0].capability) };
    } else if (id.startsWith('direct-request:')) {
      const matches = this.catalog?.commands.filter(contract => `direct-request:${JSON.stringify(contract.capability)}` === id) ?? [];
      if (matches.length !== 1) return;
      this.formLibraryDestination = { kind: 'direct-request', reference: structuredClone(matches[0].capability) };
    } else if (id.startsWith('existing:')) {
      const matches = this.workFormCommands.filter(command => `existing:${commandIdentity(command)}` === id);
      if (matches.length !== 1) return;
      this.formLibraryDestination = { kind: 'existing', reference: structuredClone(matches[0].capability) };
    } else {
      const matches = this.libraryChoices.filter(choice => JSON.stringify(choice.command.capability) === id);
      if (matches.length !== 1 || matches[0].reason) return;
      this.formLibraryDestination = { kind: 'add', reference: structuredClone(matches[0].command.capability) };
    }
    this.formLibraryOpen = false;
  }
  requestFormLibraryOwner(event: { id: string; actionId: string }): void {
    if (!this.formLibraryOpen || this.disabled || this.loading || !this.active || this.formLibrarySnapshot !== this.formLibraryContext() ||
      !this.adoptCurrentDocument()) return;
    const entries = this.formLibraryEntries.filter(entry => entry.id === event.id && entry.ownerAction?.id === event.actionId);
    if (entries.length === 1 && (event.actionId === 'requirements' || event.actionId === 'actions')) {
      this.formLibraryDestination = { kind: event.actionId }; this.formLibraryOpen = false;
    }
  }
  requestFormLibraryFooter(id: string): void {
    if (!this.formLibraryOpen || this.disabled || this.loading || this.catalogError || !this.active ||
      this.formLibrarySnapshot !== this.formLibraryContext() || !this.adoptCurrentDocument()) return;
    if (id === 'requirements' || id === 'staff') { this.formLibraryDestination = { kind: id }; this.formLibraryOpen = false; }
  }
  formLibraryClosed(reason: WorkflowLibraryClosedReason): void {
    this.formLibraryRelease?.(); this.formLibraryRelease = null;
    const destination = this.formLibraryDestination;
    this.formLibraryDestination = null; this.formLibraryOpen = false;
    if (this.destroyed || reason !== 'input' || !destination || !this.active || this.formLibrarySnapshot !== this.formLibraryContext() ||
      !this.adoptCurrentDocument()) return;
    if (destination.kind === 'requirements') this.openRequirements.emit();
    else if (destination.kind === 'staff') this.openStaffPage.emit();
    else if (destination.kind === 'actions') this.focusWorkSection('action');
    else if (destination.kind === 'preparation') this.stageProvidedForm('preparation', destination.reference);
    else if (destination.kind === 'direct-request') this.stageProvidedForm('direct-request', destination.reference);
    else if (destination.kind === 'existing' || destination.kind === 'add') {
      const reference = destination.reference;
      if (destination.kind === 'existing') {
        const matches = this.workFormCommands.filter(command => sameCapabilityReference(command.capability, reference));
        if (matches.length === 1 && this.selectForm(matches[0])) this.restoreOwnerFocus();
      } else {
        const matches = this.catalog?.commands.filter(command => sameCapabilityReference(command.capability, reference)) ?? [];
        if (matches.length !== 1) return;
        this.addAction(matches[0]);
        if (!this.error) this.restoreOwnerFocus();
      }
    }
  }
  selectWorkForm(id: string, select?: HTMLSelectElement): void {
    const matches = this.workFormCommands.filter(command => commandIdentity(command) === id);
    if (matches.length === 1) this.selectForm(matches[0]);
    if (select) select.value = this.selection.kind === 'form' ? this.selection.commandId ?? '' : '';
  }
  providedFormTitle(command: StepCommand): string {
    const matches = this.catalog?.interactions.filter(item => item.code === 'generated-command-form' && item.version === 1 &&
      sameCapabilityReference(item.command, command.capability)) ?? [];
    return matches.length === 1 && matches[0].label ? matches[0].label : this.label(command);
  }
  formTitle(command: StepCommand): string { return command.captureForm?.title || this.providedFormTitle(command); }
  formOwnerContext(): string { return this.formViewContext ? JSON.stringify(this.formViewContext()) : this.ownerKey(); }
  selectResponsibility(): boolean {
    return this.select({ kind: 'responsibility' });
  }
  selectAction(command: StepCommand): boolean {
    const selected = this.select({ kind: 'action', commandId: commandIdentity(command) });
    if (selected) { this.formActionSettings = false; this.workLibraryKind = 'action'; }
    return selected;
  }
  selectForm(command: StepCommand): boolean {
    if (this.isFinalCompletion(command)) return false;
    const selected = this.select({ kind: 'form', commandId: commandIdentity(command) });
    if (selected) { this.formActionSettings = false; this.workLibraryKind = 'form'; }
    return selected;
  }
  /** Enter the existing form/action owner without adding a command or changing its saved partition. */
  focusWorkSection(kind: 'form' | 'action'): boolean {
    if (this.scope !== 'work' || !this.active) return false;
    const commands = kind === 'form' ? this.workFormCommands : this.workActionCommands;
    if (kind === 'form' && !commands.length) {
      if (!this.select({ kind: 'library' })) return false;
      this.workLibraryKind = 'form'; this.formActionSettings = false;
      if (!this.disabled && !this.loading && !this.workEditingUnavailable && !this.catalogError) this.openFormLibrary();
      this.restoreOwnerFocus();
      return true;
    }
    const current = this.selection.kind === kind && commands.some(command => commandIdentity(command) === this.selection.commandId);
    const selected = current || (commands[0]
      ? kind === 'form' ? this.selectForm(commands[0]) : this.selectAction(commands[0])
      : kind === 'action' && (this.disabled || this.loading || this.workEditingUnavailable)
        ? this.select({ kind: 'library' }) : this.selectLibrary(kind));
    if (!selected) return false;
    this.workLibraryKind = kind; this.formActionSettings = false;
    this.restoreOwnerFocus();
    return true;
  }
  selectOutcomes(command: StepCommand, outcomeCode?: string): boolean {
    if (!this.canRoute(command))
      return this.inspectorOnly
        ? this.selectAction(command)
        : this.selectNext();
    const selected = this.select({
      kind: 'outcomes',
      commandId: commandIdentity(command),
    });
    if (selected && this.selectedCommand && sameCapabilityReference(this.selectedCommand.capability, command.capability)) {
      this.selectedRoutePin = structuredClone(command.capability);
      this.reconcileOutcomeSelection(outcomeCode);
    }
    return selected;
  }
  /** Selecting a result within the same action changes presentation only. */
  selectOutcome(command: StepCommand, outcomeCode: string): boolean {
    if (!this.active || this.overview || this.scope !== 'behavior' || this.ownerViews.focusSuspended() ||
      this.selection.kind !== 'outcomes' || this.selection.commandId !== commandIdentity(command) || !this.navigationIsCurrent()) return false;
    try {
      const document = this.currentDocument();
      if (!document || !this.ownerIsCurrent()) return false;
      this.exactCurrentRouteCommand(command, document);
      if (this.outcomes(command).filter(outcome => outcome.code === outcomeCode).length !== 1) return false;
      this.setOutcomeSelection(command, outcomeCode);
      return true;
    } catch { return false; }
  }
  selectedOutcome(command: StepCommand): StepCommand['outcomes'][number] | null {
    if (!this.selectedRoutePin || !sameCapabilityReference(this.selectedRoutePin, command.capability) || this.selection.kind !== 'outcomes' || this.selection.commandId !== commandIdentity(command)) return null;
    const matches = this.outcomes(command).filter(outcome => outcome.code === this.selection.outcomeCode);
    return matches.length === 1 ? matches[0] : null;
  }
  isOutcomeSelected(command: StepCommand, code: string): boolean { return this.selectedOutcome(command)?.code === code; }
  outcomeIdentityAvailable(command: StepCommand, code: string): boolean {
    return this.outcomes(command).filter(outcome => outcome.code === code).length === 1;
  }
  private setOutcomeSelection(command: StepCommand, code: string): void {
    this.routeFocusRequest++;
    this.selection = { kind: 'outcomes', commandId: commandIdentity(command), outcomeCode: code };
    this.selectedRoutePin = structuredClone(command.capability);
    this.routeOutcomeSelections.set(JSON.stringify(command.capability), code);
  }
  private exactCurrentRouteCommand(command: StepCommand, document: WorkflowDefinitionDocument): StepCommand {
    const buffer = this.routeBuffer;
    if (!buffer?.setupDocument || buffer.commandId !== commandIdentity(command)) return this.exactCurrentCommand(command, document);
    if (this.routePendingState(document) === 'stale') throw new Error('This staged review source changed. Keep its input or discard the setup.');
    const nodes = buffer.setupDocument.nodes.filter(node => node.id === this.node.id);
    const commands = nodes.length === 1 ? nodes[0].declaration?.behavior.commands.filter(item => commandIdentity(item) === buffer.commandId) ?? [] : [];
    if (commands.length !== 1 || !sameCapabilityReference(commands[0].capability, command.capability) || JSON.stringify(commands[0]) !== JSON.stringify(command))
      throw new Error('This staged review action changed. Keep its input or discard the setup.');
    return commands[0];
  }
  private reconcileOutcomeSelection(requestedCode?: string): void {
    const command = this.selectedCommand;
    if (this.selection.kind !== 'outcomes' || !command) return;
    if (this.selectedRoutePin && !sameCapabilityReference(this.selectedRoutePin, command.capability)) return;
    const code = requestedCode ?? this.selection.outcomeCode ?? this.routeOutcomeSelections.get(JSON.stringify(command.capability))
      ?? this.outcomes(command).find(outcome => this.outcomeIdentityAvailable(command, outcome.code))?.code;
    if (code !== undefined && (code !== this.selection.outcomeCode || !this.selectedRoutePin)) this.setOutcomeSelection(command, code);
  }
  selectRouteAction(id: string, select?: HTMLSelectElement): void {
    const command = this.routedCommands.find(
      (item) => commandIdentity(item) === id,
    );
    if (command) this.selectOutcomes(command);
    // Restore the native selection when pending edits or the owning editor block navigation.
    if (select) select.value = this.selection.commandId ?? '';
  }
  selectLibrary(kind: 'form' | 'action' = 'action'): boolean {
    if (this.scope === 'work' && kind === 'action') return this.openActionLibrary();
    if (this.scope === 'work' && kind === 'form') return this.openFormLibrary();
    if (
      this.selection.kind === 'library' &&
      this.workLibraryKind !== kind &&
      ((this.beforeSelectionChange && !this.beforeSelectionChange()) ||
        !this.applyPendingChanges())
    )
      return false;
    const selected = this.select({ kind: 'library' });
    if (selected) this.workLibraryKind = kind;
    return selected;
  }
  selectNext(): boolean {
    return this.select({ kind: 'next' });
  }
  private select(selection: typeof this.selection): boolean {
    if (
      selection.kind === this.selection.kind &&
      selection.commandId === this.selection.commandId
    ) {
      return true;
    }
    if (this.beforeSelectionChange && !this.beforeSelectionChange())
      return false;
    if (!this.adoptCurrentDocument() || !this.applyPendingChanges()) return false;
    this.routeFocusRequest++;
    this.selection = selection;
    this.error = '';
    this.message = '';
    return true;
  }
  editForm(command: StepCommand): void {
    if (!this.navigationIsCurrent()) return;
    if (this.isFinalCompletion(command)) return;
    if (this.scope === 'work') {
      if (this.formActionSettings) this.backToWorkForm(); else this.selectForm(command);
      return;
    }
    if (
      (!this.beforeSelectionChange || this.beforeSelectionChange()) &&
      this.applyPendingChanges()
    )
      this.openForm.emit(command);
  }
  showActions(command?: StepCommand): void {
    if (
      (!this.beforeSelectionChange || this.beforeSelectionChange()) &&
      this.applyPendingChanges()
    )
      this.openActions.emit(command);
  }
  requestOutcomes(command: StepCommand): void {
    if (
      (!this.beforeSelectionChange || this.beforeSelectionChange()) &&
      this.applyPendingChanges()
    )
      this.openOutcomes.emit(command);
  }
  showAdvanced(): void {
    if (
      (!this.beforeSelectionChange || this.beforeSelectionChange()) &&
      this.applyPendingChanges()
    )
      this.openAdvanced.emit();
  }
  showPaths(): void {
    if (
      this.navigationIsCurrent() && (!this.beforeSelectionChange || this.beforeSelectionChange()) &&
      this.applyPendingChanges()
    )
      this.openPaths.emit();
  }
  showSelectedOutcomePath(): void {
    const selected = this.settleSelectedOutcome();
    if (selected && !this.routeInspectionIssue(selected.command)) this.openOutcomePath.emit(selected);
  }
  showSelectedOutcomeReason(): void {
    const selected = this.settleSelectedOutcome();
    if (selected) this.openActions.emit(selected.command);
  }
  private settleSelectedOutcome(): { command: StepCommand; outcomeCode: string } | null {
    const selected = this.selectedCommand, code = this.selection.outcomeCode;
    if (!selected || !this.selectedOutcome(selected) || code === undefined || !this.active || this.overview || this.scope !== 'behavior' ||
      this.ownerViews.focusSuspended() || !this.navigationIsCurrent() ||
      (this.beforeSelectionChange && !this.beforeSelectionChange()) || !this.applyPendingChanges()) return null;
    const document = this.currentDocument();
    if (!document || !this.ownerIsCurrent() || !this.navigationIsCurrent() || this.selection.kind !== 'outcomes' ||
      this.selection.commandId !== commandIdentity(selected) || this.selection.outcomeCode !== code || this.ownerViews.focusSuspended() ||
      !this.active || this.overview || this.scope !== 'behavior') return null;
    const nodes = document.nodes.filter(node => node.id === this.node.id);
    const commands = nodes.length === 1 ? nodes[0].declaration?.behavior.commands.filter(command => commandIdentity(command) === commandIdentity(selected)
      && sameCapabilityReference(command.capability, selected.capability)) ?? [] : [];
    if (commands.length !== 1 || commands[0].outcomes.filter(outcome => outcome.code === code).length !== 1) return null;
    this.document = document; this.node = nodes[0];
    return { command: commands[0], outcomeCode: code };
  }
  showDirectPath(): void {
    const latest = this.currentDocument();
    if (!this.active || this.overview || this.scope !== 'behavior' || this.selection.kind !== 'next' || this.ownerViews.focusSuspended() ||
      !latest || this.routingFingerprint(latest) !== this.routingFingerprint() || !this.navigationIsCurrent() ||
      (this.beforeSelectionChange && !this.beforeSelectionChange()) || !this.applyPendingChanges()) return;
    if (this.ownerIsCurrent() && this.navigationIsCurrent() && this.selection.kind === 'next' && !this.ownerViews.focusSuspended() &&
      this.nextRoute.state === 'direct') this.openDirectPath.emit();
  }
  showWorkflowStatuses(): void {
    if (this.loading || (!this.immediateRoutingChanges && this.disabled)) return;
    if (this.externalWorkflowStatuses) {
      const owner = JSON.stringify(this.ownerContext?.()) ?? '', view = this.workflowStatusesView?.();
      const available = () => !!this.ownerContext && !!view && view === this.externalWorkflowStatusesView
        && view === this.workflowStatusesView?.() && owner === JSON.stringify(this.ownerContext?.())
        && this.active && !this.overview && this.scope === 'behavior' && !this.loading
        && !this.ownerViews.focusSuspended() && this.ownerIsCurrent() && this.navigationIsCurrent();
      if (!available() || (this.beforeSelectionChange && !this.beforeSelectionChange()) || !this.applyPendingChanges() || !available()) return;
      const document = this.currentDocument();
      const nodes = document?.nodes.filter(node => node?.id === this.node.id) ?? [];
      if (!document || nodes.length !== 1 || nodes[0].typeCode !== 'human_step') return;
      this.workflowStatusesOwnerRequested.emit({ nodeId: this.node.id, origin: 'routing', owner, source: JSON.stringify(document), view: view! });
      return;
    }
    if (
      (!this.beforeSelectionChange || this.beforeSelectionChange()) &&
      this.applyPendingChanges()
    ) {
      if (this.immediateRoutingChanges && this.scope === 'behavior') {
        this.workflowStatusesOpen = true;
        this.focusWorkflowStatuses(true);
      } else this.openWorkflowStatuses.emit(this.node.id);
    }
  }
  closeWorkflowStatuses(): void {
    if (!this.applyPendingChanges()) return;
    this.workflowStatusesOpen = false;
    this.focusWorkflowStatuses(false);
  }
  private focusWorkflowStatuses(open: boolean): void {
    const request = ++this.statusFocusRequest,
      nodeId = this.node.id;
    setTimeout(() => {
      if (
        this.ownerViews.focusSuspended() ||
        request !== this.statusFocusRequest ||
        this.node.id !== nodeId ||
        this.workflowStatusesOpen !== open ||
        this.scope !== 'behavior'
      )
        return;
      this.element.nativeElement
        .querySelector<HTMLElement>(
          open ? '.routing-statuses' : '[data-workflow-statuses-trigger]',
        )
        ?.focus();
    });
  }
  workflowStatusesPending(): boolean {
    return !!this.statusEditor?.hasPendingChanges();
  }
  routeChangesPending(): boolean {
    return !!this.routeBuffer;
  }
  onWorkflowStatusesPending(pending: boolean): void {
    this.pendingChangesChange.emit(
      pending || !!this.formSetup || !!this.routeBuffer || !!this.dataEditor?.hasPendingChanges(),
    );
  }
  onWorkflowStatusesDocumentChange(next: WorkflowDefinitionDocument): void {
    // Global status edits use the status editor's own scope and stale checks.
    // Retained action identities must not prevent an unrelated valid status edit.
    if (
      this.disabled ||
      this.loading ||
      this.documentContext(next) !== this.documentContext(this.document)
    ) {
      if (this.statusEditor) {
        this.statusEditor.document = this.document;
        this.statusEditor.discardPendingChanges();
      }
      this.error =
        'The workflow editing context changed. Reopen its statuses before making changes.';
      return;
    }
    try {
      this.document = replaceStatusConfiguration(
        this.document,
        null,
        next.statusConfiguration ?? emptyStatusConfiguration(),
      );
    } catch (error) {
      this.error =
        error instanceof Error ? error.message : 'Review workflow statuses.';
      return;
    }
    this.node =
      this.document.nodes.find((node) => node.id === this.node.id) ?? this.node;
    this.error = '';
    this.message = '';
    this.documentChange.emit(this.document);
  }
  private applyWorkflowStatuses(): boolean {
    return (
      !this.statusEditor?.hasPendingChanges() ||
      this.statusEditor.applyPendingChanges(this.document)
    );
  }
  hasPendingChanges(): boolean {
    return (
      this.formSetup !== null ||
      this.routeBuffer !== null ||
      !!this.dataEditor?.hasPendingChanges() ||
      this.workflowStatusesPending()
    );
  }
  /** Recovery text only: retain every unapplied path and setup intent without applying or replaying it. */
  capturePendingRouteInputs(): readonly { name: string; label: string; value: string }[] {
    const buffer = this.routeBuffer;
    if (!buffer) return [];
    const staged = !!buffer.setupDocument;
    return [{
      name: `workflow-routing-input:${encodeURIComponent(this.node.id)}:${this.scope}:${encodeURIComponent(buffer.commandId)}`,
      label: `Unapplied ${staged ? 'review setup' : 'routing'} for ${this.node.displayName || this.node.stepCode || this.node.id}`,
      value: JSON.stringify({
        format: 'phms.workflow-routing-input', version: 1,
        kind: staged ? 'staged-review-setup' : 'route-input',
        nodeId: this.node.id, scope: this.scope, sourceContext: buffer.context, sourceNodeType: buffer.nodeType,
        pendingCommand: buffer.command, returnCodes: [...buffer.returns], workflowStatuses: buffer.workflowStatuses,
        ...(buffer.setupDocument ? { setupDocument: buffer.setupDocument } : {}),
        originalCommand: buffer.original,
        ...(buffer.originalDeclaration === undefined ? {} : { originalDeclaration: buffer.originalDeclaration }),
        originalPaths: buffer.originalPaths, originalWorkflowStatuses: buffer.originalWorkflowStatuses,
      }),
    }];
  }
  /** Each adapter owns only its buffer; staged review Add remains deliberate. */
  pendingEditorScopes(
    latestDocument: () => WorkflowDefinitionDocument | null = () => this.currentDocument(),
  ): readonly WorkflowEditorPendingScope[] {
    const routeScope = (candidate: boolean): WorkflowEditorPendingScope => ({
      id: candidate ? 'review-setup-add' : 'route-input',
      label: candidate ? 'staged review decision Add' : 'outcome route input',
      kind: candidate ? 'modal-candidate' : 'input',
      state: () => !this.routeBuffer || !!this.routeBuffer.setupDocument !== candidate
        ? 'clean' : !this.ownerIsCurrent() ? 'stale' : this.routePendingState(latestDocument()),
      fingerprint: () => JSON.stringify([candidate, !!this.routeBuffer?.setupDocument === candidate && !!this.routeBuffer
        ? this.routePendingFingerprint(latestDocument()) : null, this.disabled, this.loading, this.inputOwner, this.ownerKey()]),
      ...(candidate ? {} : { apply: () => this.applyRouteChanges(latestDocument() ?? undefined) }),
      discard: () => {
        if (this.routeBuffer && !!this.routeBuffer.setupDocument === candidate) this.discardRouteChanges();
        this.refreshSettledSource(latestDocument() ?? this.document);
      },
      focus: () => this.focusRouteChanges(),
    });
    const currentDataCommand = (document: WorkflowDefinitionDocument): StepCommand | null => {
      const owner = this.dataEditor?.command;
      if (!owner) return null;
      const nodes = document.nodes.filter(node => node.id === this.node.id);
      const matches = nodes.length === 1 ? nodes[0].declaration?.behavior.commands.filter(command =>
        commandIdentity(command) === commandIdentity(owner) && sameCapabilityReference(command.capability, owner.capability)) ?? [] : [];
      return matches.length === 1 ? matches[0] : null;
    };
    const dataScope = () => this.dataEditor?.pendingEditorScopes(latestDocument, currentDataCommand)[0];
    const statusScope = () => this.statusEditor?.pendingEditorScopes(latestDocument)[0];
    const delegated = (kind: 'data' | 'statuses'): WorkflowEditorPendingScope => {
      const child = kind === 'data' ? dataScope : statusScope;
      return {
        id: kind === 'data' ? 'action-data' : 'workflow-statuses',
        label: kind === 'data' ? 'action data flow' : 'workflow status input',
        state: () => {
          const state = child()?.state() ?? 'clean';
          return state === 'clean' ? state : !this.ownerIsCurrent() ? 'stale' : state === 'stale' ? state :
            this.disabled || this.loading || this.workEditingUnavailable ? 'invalid' : state;
        },
        fingerprint: () => JSON.stringify([this.disabled, this.loading, this.inputOwner, this.ownerKey(), child()?.fingerprint() ?? null]),
        apply: () => {
          if (!this.synchronizePendingDocument(latestDocument())) return false;
          if (this.disabled || this.loading || this.workEditingUnavailable) return false;
          return child()?.apply?.() ?? true;
        },
        discard: () => {
          child()?.discard();
          this.refreshSettledSource(latestDocument() ?? this.document);
          this.pendingChangesChange.emit(this.hasPendingChanges());
        },
        focus: () => {
          if (kind === 'statuses') this.workflowStatusesOpen = true;
          child()?.focus();
        },
      };
    };
    const candidateScope = (kind: 'preparation' | 'direct-request'): WorkflowEditorPendingScope => ({
      id: kind === 'preparation' ? 'preparation-form-add' : 'direct-request-form-add',
      label: kind === 'preparation' ? 'staged Preparation form change' : 'staged review request form change', kind: 'modal-candidate',
      state: () => this.formSetup?.kind === kind ? this.stagedFormPendingState(latestDocument()) : 'clean',
      fingerprint: () => JSON.stringify([kind, this.formSetup?.kind === kind ? this.formSetup : null,
        this.formSetup?.kind === kind && latestDocument(), this.reviewSetupCatalog(), this.inputOwner, this.ownerKey(), this.disabled, this.loading, this.scope]),
      discard: () => { this.discardStagedForm(kind); this.refreshSettledSource(latestDocument() ?? this.document); },
      focus: () => this.focusStagedForm(kind),
    });
    return [routeScope(false), routeScope(true), candidateScope('preparation'), candidateScope('direct-request'), delegated('data'), delegated('statuses')];
  }
  private synchronizePendingDocument(document: WorkflowDefinitionDocument | null): boolean {
    if (!this.ownerIsCurrent() || !document || this.documentContext(document) !== this.documentContext(this.document)) return false;
    const matches = document.nodes.filter(node => node.id === this.node.id);
    if (matches.length !== 1) return false;
    this.document = document;
    this.node = matches[0];
    return true;
  }
  private routePendingFingerprint(document: WorkflowDefinitionDocument | null): string {
    const buffer = this.routeBuffer;
    return JSON.stringify([buffer && { ...buffer, returns: [...buffer.returns].sort() },
      document && [this.documentContext(document), document.nodes, document.edges, document.capabilityReferences, document.statusConfiguration],
      buffer && this.catalog?.commands.filter(command => commandIdentity(command) === buffer.commandId),
      buffer?.setupDocument && this.reviewSetupCatalog(),
      this.disabled, this.loading]);
  }
  private routePendingState(document: WorkflowDefinitionDocument | null): WorkflowEditorBufferState {
    const buffer = this.routeBuffer;
    if (!buffer) return 'clean';
    if (buffer.setupDocument && buffer.setupCatalog !== this.reviewSetupCatalog()) return 'stale';
    if (!document || this.documentContext(document) !== buffer.context) return 'stale';
    const nodes = document.nodes.filter(node => node.id === this.node.id);
    if (nodes.length !== 1 || nodes[0].typeCode !== buffer.nodeType) return 'stale';
    const node = nodes[0];
    const commands = (buffer.setupDocument?.nodes.find(item => item.id === node.id) ?? node).declaration?.behavior.commands
      .filter(command => commandIdentity(command) === buffer.commandId) ?? [];
    if (commands.length !== 1 || JSON.stringify(commands[0]) !== buffer.original ||
      (buffer.originalDeclaration && JSON.stringify(node.declaration ?? null) !== buffer.originalDeclaration) ||
      this.routingFingerprint(document) !== buffer.originalPaths ||
      JSON.stringify(document.statusConfiguration ?? null) !== buffer.originalWorkflowStatuses) return 'stale';
    if (this.disabled || this.loading || !hasEditableRouting(document)) return 'invalid';
    const contracts = this.catalog?.commands.filter(contract => sameCapabilityReference(contract.capability, commands[0].capability)) ?? [];
    if (contracts.length !== 1 || buffer.command.outcomes.some(outcome => !outcome.label.trim() || !outcome.destinationNodeId)) return 'invalid';
    try {
      const base = this.routingBase(document), contract = contracts[0];
      const routed = applyComponentRoutes(contract.completesStep === false ? base : this.changeCommand(commands[0], { outcomes: buffer.command.outcomes }, base),
        node.id, buffer.command, contract, buffer.returns);
      applyStepOutcomeStatuses(routed, node.id, buffer.workflowStatuses, buffer.command.outcomes.map(outcome => outcome.code));
      return 'valid';
    } catch { return 'invalid'; }
  }
  resetPendingChanges(): void {
    this.dataEditor?.resetPendingChanges();
    this.statusEditor?.discardPendingChanges();
    this.discardRouteChanges();
    this.discardStagedForm();
    this.refreshSettledSource();
  }
  /** Reset only route/setup input; action data and workflow status edits retain ownership. */
  discardRouteChanges(): void {
    this.routeFocusRequest++;
    const hadSetup = !!this.routeBuffer?.setupDocument;
    if (hadSetup) { this.reviewSetupDialogOpen = false; this.reviewSetupOrigin = null; this.reviewSetupDialogError = ''; }
    this.routeBuffer = null;
    this.routeError = '';
    if (hadSetup) {
      const routed = this.routedCommands[0];
      this.selection = routed
        ? { kind: 'outcomes', commandId: commandIdentity(routed) }
        : { kind: 'next' };
      this.reconcileOutcomeSelection();
    }
    this.pendingChangesChange.emit(this.hasPendingChanges());
    this.refreshSettledSource();
  }
  applyPendingChanges(latestDocument?: WorkflowDefinitionDocument): boolean {
    if (this.hasPendingChanges() && !this.ownerIsCurrent()) {
      this.error = 'The draft or author context changed. Pending action input is retained; discard its scoped input to review the current source before editing again.';
      this.focusPendingChanges(); return false;
    }
    return this.applyPendingChangesCore(latestDocument ?? this.latestDocument?.() ?? undefined, false);
  }
  private applyPendingChangesCore(latestDocument: WorkflowDefinitionDocument | undefined, allowReviewSetup: boolean): boolean {
    if (this.formSetup) {
      this.formSetupError = `Use ${this.stagedFormApplyLabel} to accept this change, or discard its setup.`;
      this.focusStagedForm(); return false;
    }
    if (this.routeBuffer?.setupDocument && !allowReviewSetup) {
      this.routeError = `Use ${this.reviewSetupPrimaryLabel} in the setup dialog, or discard this setup.`;
      this.focusRouteChanges();
      return false;
    }
    if (latestDocument && latestDocument !== this.document) {
      const matching = latestDocument.nodes.filter(
        (node) => node.id === this.node.id,
      );
      if (matching.length !== 1) {
        this.error =
          'This step changed. Reopen it before applying action edits.';
        this.focusPendingChanges();
        return false;
      }
      this.document = latestDocument;
      this.node = matching[0];
      // A sibling editor may have committed before Angular updates this child.
      // Validate the buffered action against the latest command and process data.
      if (this.dataEditor?.hasPendingChanges()) {
        const command = this.selectedCommand;
        if (!command) return false;
        this.dataEditor.document = latestDocument;
        this.dataEditor.command = command;
      }
    }
    if (this.workEditingUnavailable && this.hasPendingChanges()) return false;
    if (!this.applyWorkflowStatuses()) return false;
    if (this.dataEditor?.hasPendingChanges()) {
      const previousDisabled = this.dataEditor.disabled;
      this.dataEditor.disabled =
        this.disabled || this.loading || this.workEditingUnavailable;
      try {
        if (!this.dataEditor.applyPendingChanges()) return false;
      } finally {
        this.dataEditor.disabled = previousDisabled;
      }
    }
    return this.applyRouteChangesCore(undefined, allowReviewSetup);
  }
  /** Settle route input only; sibling action/status buffers retain their input. */
  applyRouteChanges(latestDocument?: WorkflowDefinitionDocument): boolean {
    if (this.routeBuffer && !this.ownerIsCurrent()) {
      this.routeError = 'The draft or author context changed. Route input is retained; discard only its scoped input to review the current source.';
      return false;
    }
    return this.applyRouteChangesCore(latestDocument, false);
  }
  private applyRouteChangesCore(latestDocument: WorkflowDefinitionDocument | undefined, allowReviewSetup: boolean): boolean {
    if (!this.routeBuffer) return true;
    if (this.routeBuffer.setupDocument && !allowReviewSetup) return false;
    if (latestDocument && !this.synchronizePendingDocument(latestDocument)) return false;
    if (this.disabled || this.loading) return false;
    const buffer = this.routeBuffer;
    if (buffer.setupDocument && buffer.setupCatalog !== this.reviewSetupCatalog()) {
      this.routeError = 'Installed review forms or their required actions changed. Discard this setup and choose the current review form again.';
      this.focusRouteChanges();
      return false;
    }
    if (this.documentContext(this.document) !== buffer.context ||
      this.document.nodes.filter(node => node.id === this.node.id && node.typeCode === buffer.nodeType).length !== 1) {
      this.routeError = 'This step changed. Reopen it before applying outcome routes.';
      this.focusRouteChanges();
      return false;
    }
    if (
      JSON.stringify(this.document.statusConfiguration ?? null) !==
      buffer.originalWorkflowStatuses
    ) {
      this.routeError =
        'Workflow statuses changed while these outcomes were being edited. Discard these edits and review the current statuses.';
      this.focusRouteChanges();
      return false;
    }
    const matches = this.commands.filter(
      (command) => commandIdentity(command) === buffer.commandId,
    );
    const current = matches.length === 1 ? matches[0] : null;
    if (
      !current ||
      JSON.stringify(current) !== buffer.original ||
      (buffer.originalDeclaration &&
        JSON.stringify(this.node.declaration ?? null) !==
          buffer.originalDeclaration) ||
      this.routingFingerprint() !== buffer.originalPaths
    ) {
      this.routeError =
        'This step or its paths changed while outcomes were being edited. Discard these edits and reopen its outcomes.';
      this.focusRouteChanges();
      return false;
    }
    const contract = this.contract(current);
    if (!contract) {
      this.routeError =
        'Restore this action’s installed module before changing outcomes.';
      this.focusRouteChanges();
      return false;
    }
    if (
      buffer.command.outcomes.some(
        (outcome) => !outcome.label.trim() || !outcome.destinationNodeId,
      )
    ) {
      this.routeError =
        'Give every outcome a label and destination before applying these routes.';
      this.focusRouteChanges();
      return false;
    }
    const selectedOutcomeCode = this.selection.commandId === buffer.commandId ? this.selection.outcomeCode : undefined;
    const applied = this.edit(() => {
      const base = this.routingBase();
      const routed = applyComponentRoutes(
        contract.completesStep === false
          ? base
          : this.changeCommand(
              current,
              { outcomes: buffer.command.outcomes },
              base,
            ),
        this.node.id,
        buffer.command,
        contract,
        buffer.returns,
      );
      const next = applyStepOutcomeStatuses(
        routed,
        this.node.id,
        buffer.workflowStatuses,
        buffer.command.outcomes.map((outcome) => outcome.code),
      );
      // The parent accepts persisted draft changes only after local buffers are settled.
      this.discardRouteChanges();
      this.selection = { kind: 'outcomes', commandId: buffer.commandId, outcomeCode: selectedOutcomeCode };
      this.reconcileOutcomeSelection();
      return next;
    });
    if (!applied) {
      this.routeError = this.error;
      this.error = '';
      this.focusRouteChanges();
      return false;
    }
    this.discardRouteChanges();
    return true;
  }
  focusPendingChanges(): void {
    if (this.ownerViews.focusSuspended()) return;
    if (this.formSetup) { this.focusStagedForm(); return; }
    if (this.statusEditor?.hasPendingChanges()) {
      this.statusEditor.focusPendingChanges();
      return;
    }
    if (this.dataEditor?.hasPendingChanges()) {
      this.dataEditor.focusPendingChanges();
      return;
    }
    this.focusRouteChanges();
  }
  focusRouteChanges(): void {
    if (this.ownerViews.focusSuspended() || !this.routeBuffer || !this.ownerIsCurrent()) return;
    if (this.routeBuffer.setupDocument && this.reviewSetupOrigin) { this.reopenReviewSetupDialog(); return; }
    const buffer = this.routeBuffer;
    const invalid = buffer.command.outcomes.find(outcome => !outcome.label.trim() || !outcome.destinationNodeId);
    this.selection = { kind: 'outcomes', commandId: buffer.commandId, outcomeCode: invalid?.code ?? this.selection.outcomeCode };
    this.reconcileOutcomeSelection();
    const request = ++this.routeFocusRequest, focusRequest = this.ownerFocusRequest;
    const focused = this.element.nativeElement.ownerDocument.activeElement;
    const snapshot = () => JSON.stringify([this.ownerKey(), this.inputOwner, this.node.id, this.scope, this.selection,
      this.currentDocument(), this.catalog, this.routePendingFingerprint(this.currentDocument())]);
    const captured = snapshot();
    setTimeout(() => {
        const host = this.element.nativeElement;
        const active = host.ownerDocument.activeElement;
        if (request !== this.routeFocusRequest || focusRequest !== this.ownerFocusRequest || this.destroyed || !this.active || this.overview ||
          this.scope !== 'behavior' || !host.isConnected || host.closest('[hidden],[inert]') || this.ownerViews.focusSuspended() ||
          this.routeBuffer !== buffer || !this.ownerIsCurrent() || captured !== snapshot()) return;
        if (active !== focused && active !== host.ownerDocument.body && active !== host.ownerDocument.documentElement &&
          active?.isConnected && !active.closest('[hidden],[inert]')) return;
        const card = Array.from(
          host.querySelectorAll<HTMLElement>(
            '[data-outcome-code]',
          ),
        ).find((item) => item.dataset['outcomeCode'] === invalid?.code);
        const control = card?.querySelector<HTMLElement>(
          invalid?.label.trim() ? 'select.destination-select' : 'input',
        );
        const details = control?.closest('details');
        if (details) details.open = true;
        (control && !control.matches(':disabled') ? control : host.querySelector<HTMLElement>('.route-error'))?.focus({ preventScroll: true });
      });
  }
  applyOutcomes(command: StepCommand): boolean {
    if (this.disabled || this.loading || !this.bufferOutcomes(command))
      return false;
    return this.applyPendingChangesCore(undefined, true);
  }
  private bufferOutcomes(command: StepCommand) {
    if (!this.applyWorkflowStatuses()) return null;
    const commandId = commandIdentity(command);
    if (this.routeBuffer?.commandId !== commandId) {
      if (!this.applyPendingChanges()) return null;
      // A committed edit can precede Angular refreshing the row's command object.
      // Rebase only a new buffer; an active buffer must keep its stale-edit guards.
      const matches = this.commands.filter(
        (current) =>
          commandIdentity(current) === commandId &&
          sameCapabilityReference(current.capability, command.capability),
      );
      if (matches.length !== 1) {
        this.routeError =
          'This action changed. Reopen its outcomes before editing routes.';
        return null;
      }
      command = matches[0];
      if (!this.canRoute(command)) return null;
      let outcomes: StepCommand['outcomes'];
      let workflowStatuses: Record<string, string>;
      try {
        outcomes = this.projectedOutcomes(command);
        workflowStatuses = this.readOutcomeWorkflowStatuses(command);
      } catch (error) {
        this.routeError =
          error instanceof Error
            ? error.message
            : 'Inspect workflow paths before editing outcomes.';
        return null;
      }
      this.routeBuffer = {
        context: this.documentContext(this.document),
        nodeType: this.node.typeCode,
        commandId: commandIdentity(command),
        original: JSON.stringify(command),
        originalPaths: this.routingFingerprint(),
        originalWorkflowStatuses: JSON.stringify(
          this.document.statusConfiguration ?? null,
        ),
        workflowStatuses,
        immediateEligible: !this.routeIssue(command),
        savedRouteSummaries: Object.fromEntries(outcomes.map(outcome => [outcome.code, outcome.destinationNodeId
          ? `${this.returnCodes(command).has(outcome.code) ? 'Return to' : 'Continue forward to'} ${this.destinationLabel(outcome.destinationNodeId)}` : 'No destination saved'])),
        command: { ...structuredClone(command), outcomes },
        returns: this.returnCodes(command),
      };
      this.pendingChangesChange.emit(true);
    }
    return this.routeBuffer;
  }
  get workflowStatusChoices() {
    return this.document.statusConfiguration?.statuses ?? [];
  }
  outcomeWorkflowStatus(command: StepCommand, code: string): string {
    return this.routeBuffer?.commandId === commandIdentity(command)
      ? (this.routeBuffer.workflowStatuses[code] ?? '')
      : readStepOutcomeStatus(this.document, this.node.id, code).statusCode;
  }
  outcomeWorkflowStatusError(code: string): string | null {
    return readStepOutcomeStatus(this.document, this.node.id, code).error;
  }
  unavailableOutcomeWorkflowStatus(
    command: StepCommand,
    code: string,
  ): string | null {
    const statusCode = this.outcomeWorkflowStatus(command, code);
    return statusCode &&
      !this.workflowStatusChoices.some((status) => status.code === statusCode)
      ? statusCode
      : null;
  }
  get workflowStatusNotice(): string | null {
    return stepOutcomeStatusNotice(this.document, this.node.id);
  }
  setOutcomeWorkflowStatus(
    command: StepCommand,
    code: string,
    statusCode: string,
  ): void {
    if (
      this.disabled ||
      this.loading ||
      !command.outcomes.some((outcome) => outcome.code === code)
    )
      return;
    const buffer = this.bufferOutcomes(command);
    if (!buffer) return;
    buffer.workflowStatuses[code] = statusCode;
    this.routeError = '';
    this.pendingChangesChange.emit(true);
  }
  private readOutcomeWorkflowStatuses(
    command: StepCommand,
  ): Record<string, string> {
    return Object.fromEntries(
      command.outcomes.map((outcome) => {
        const status = readStepOutcomeStatus(
          this.document,
          this.node.id,
          outcome.code,
        );
        if (status.error) throw new Error(status.error);
        return [outcome.code, status.statusCode];
      }),
    );
  }
  outcomes(command: StepCommand): StepCommand['outcomes'] {
    if (this.routeBuffer?.commandId === commandIdentity(command))
      return this.routeBuffer.command.outcomes;
    try {
      return this.projectedOutcomes(command);
    } catch {
      return command.outcomes;
    }
  }
  private projectedOutcomes(command: StepCommand): StepCommand['outcomes'] {
    const contract = this.contract(command);
    return contract?.completesStep === false && this.canRoute(command)
      ? readStepOutcomeRoutes(
          this.routingBase(),
          this.node.id,
          command,
          contract,
        )
      : structuredClone(command.outcomes);
  }
  isRecordedRouting(command: StepCommand): boolean {
    const contract = this.contract(command);
    return (
      contract?.completesStep === false &&
      !!contract.decisionEvidence &&
      !!contract.condition
    );
  }
  private canRoute(command: StepCommand): boolean {
    return (
      !!command.outcomes.length &&
      (this.contract(command)?.completesStep !== false ||
        this.isRecordedRouting(command))
    );
  }
  get reviewDecisionAvailable(): boolean {
    return !!this.catalog?.commands.some(
      (contract) =>
        contract.capability.key.provider === 'workflow' &&
        contract.capability.key.code === 'workflow.review.record' &&
        contract.completesStep === false &&
        !!contract.condition &&
        !!contract.decisionEvidence,
    );
  }
  get preparingReviewDecision(): boolean {
    return !!this.routeBuffer?.setupDocument;
  }
  reviewSetupDialogContext(): string {
    if (!this.routeBuffer?.setupDocument) return JSON.stringify([this.reviewSetupGeneration, this.ownerKey(), this.scope, this.active, this.overview]);
    return JSON.stringify([this.reviewSetupGeneration, this.ownerKey(), this.scope, this.active, this.overview,
      this.disabled, this.loading, this.currentDocument(), this.reviewSetupCatalog()]);
  }
  private reviewSetupCommand(): StepCommand | null {
    const buffer = this.routeBuffer;
    if (!buffer?.setupDocument) return null;
    const nodes = buffer.setupDocument.nodes.filter(node => node.id === this.node.id);
    const commands = nodes.length === 1 && Array.isArray(nodes[0].declaration?.behavior.commands)
      ? nodes[0].declaration!.behavior.commands.filter(command => commandIdentity(command) === buffer.commandId) : [];
    return commands.length === 1 ? commands[0] : null;
  }
  private reviewSetupOwnerCurrent(): boolean {
    const origin = this.reviewSetupOrigin;
    return !!origin && origin.buffer === this.routeBuffer && !!this.routeBuffer?.setupDocument && origin.owner === this.ownerKey()
      && !this.destroyed && this.active && !this.overview && this.scope === 'behavior' && !this.disabled && !this.loading
      && !this.catalogError && this.ownerIsCurrent() && !this.workflowStatusesPending() && !this.dataEditor?.hasPendingChanges();
  }
  get reviewSetupDialogModel(): WorkflowReviewSetupDialogModel | null {
    const buffer = this.routeBuffer, origin = this.reviewSetupOrigin, command = this.reviewSetupCommand();
    if (!buffer?.setupDocument || !origin || origin.buffer !== buffer || !command || !this.catalog) return null;
    const summary = readWorkflowReviewSetupSummary(origin.baseline, buffer.setupDocument, this.node.id, command.capability, this.catalog);
    if (!summary) return null;
    const current = this.currentDocument();
    return { ...summary, canApply: this.reviewSetupOwnerCurrent() && this.routePendingState(current) === 'valid',
      outcomes: buffer.command.outcomes.map(outcome => {
        const movement = buffer.returns.has(outcome.code) ? 'return' as const : 'forward' as const;
        let choices: { id: string; label: string }[] = [];
        try { choices = this.outcomeDestinations(command, outcome.code).map(node => ({ id: node.id, label: this.destinationLabel(node.id) })); } catch { /* Retain the chosen destination for inspection. */ }
        return { code: outcome.code, label: outcome.label || outcome.code, movement, destinationNodeId: outcome.destinationNodeId, choices,
          ...(outcome.destinationNodeId && !choices.some(choice => choice.id === outcome.destinationNodeId)
            ? { savedUnavailable: this.unavailableDestinationLabel(outcome.destinationNodeId) } : {}) };
      }) };
  }
  get reviewSetupPrimaryLabel(): string { return this.reviewSetupDialogModel?.primaryLabel ?? 'Review setup'; }
  get reviewSetupDialogDisabled(): boolean { return !this.reviewSetupOwnerCurrent() || this.routePendingState(this.currentDocument()) === 'stale'; }
  get reviewSetupAvailabilityIssue(): string {
    if (!this.routeBuffer?.setupDocument) return '';
    if (!this.reviewSetupDialogModel) return 'This setup cannot be summarized safely. Its source is preserved; discard it and choose the current review setup again.';
    return this.reviewSetupDialogDisabled ? 'The draft, author or installed choices changed. Discard this setup and review the current source before adding it.' : '';
  }

  reviewSetupDialogOpened(event: { context: string }): void {
    if (event.context !== this.reviewSetupDialogContext() || !this.reviewSetupDialogOpen || this.reviewSetupLease) return;
    if (!this.reviewSetupOrigin || this.reviewSetupOrigin.buffer !== this.routeBuffer) return;
    this.reviewSetupOrigin.openedContext = event.context;
    this.reviewSetupLease = { context: event.context, release: this.ownerViews.suspendFocus() };
  }
  reviewSetupDialogClosed(event: { context: string; reason: 'dismissed' | 'input' | 'context' | 'inactive' | 'destroyed' }): void {
    if (this.reviewSetupLease?.context === event.context) { this.reviewSetupLease.release(); this.reviewSetupLease = null; }
    if (this.destroyed) return;
    const applied = this.reviewSetupAppliedFocus;
    if (applied?.context === event.context) {
      this.reviewSetupAppliedFocus = null;
      const request = ++this.routeFocusRequest, focused = this.element.nativeElement.ownerDocument.activeElement;
      setTimeout(() => {
        const host = this.element.nativeElement, active = host.ownerDocument.activeElement;
        if (this.destroyed || !this.active || this.overview || this.scope !== 'behavior' || this.ownerViews.focusSuspended()
          || request !== this.routeFocusRequest || applied.owner !== this.ownerKey() || applied.document !== JSON.stringify(this.currentDocument())
          || this.selection.kind !== 'outcomes' || this.selection.commandId !== applied.commandId || !host.isConnected || host.closest('[hidden],[inert]')) return;
        if (active !== focused && active !== host.ownerDocument.body && active !== host.ownerDocument.documentElement
          && active?.isConnected && !active.closest('[hidden],[inert]')) return;
        Array.from(host.querySelectorAll<HTMLButtonElement>('[data-route-code]')).find(button => button.dataset['routeCode'] === this.selection.outcomeCode)?.focus({ preventScroll: true });
      });
    }
    const origin = this.reviewSetupOrigin;
    if (!origin || origin.buffer !== this.routeBuffer || origin.openedContext !== event.context) return;
    this.reviewSetupDialogOpen = false;
    if (event.reason === 'dismissed' && origin.owner === this.ownerKey()) {
      this.discardRouteChanges();
      const current = () => JSON.stringify([this.reviewSetupGeneration, this.ownerKey(), this.currentDocument(), this.scope, this.active, this.overview]);
      const captured = current(), request = ++this.routeFocusRequest, focused = this.element.nativeElement.ownerDocument.activeElement;
      setTimeout(() => {
        const host = this.element.nativeElement, active = host.ownerDocument.activeElement;
        if (this.destroyed || request !== this.routeFocusRequest || captured !== current() || this.ownerViews.focusSuspended()
          || !host.isConnected || host.closest('[hidden],[inert]')) return;
        if (active !== focused && active !== host.ownerDocument.body && active !== host.ownerDocument.documentElement
          && active?.isConnected && !active.closest('[hidden],[inert]')) return;
        const original = origin.returnFocus;
        const target = original?.isConnected && !original.closest('[hidden],[inert]') ? original
          : host.querySelector<HTMLElement>(`[data-review-setup-trigger="${origin.returnKind}"]`);
        const disclosure = target?.closest('details'); if (disclosure) disclosure.open = true;
        if (target && !target.matches(':disabled') && !target.closest('[hidden],[inert]')) target.focus();
      });
    } else if (event.reason !== 'input') this.reviewSetupDialogError = 'The setup context changed. Its input is retained; review the current source or discard this setup.';
  }
  reopenReviewSetupDialog(): void {
    if (!this.routeBuffer?.setupDocument || this.destroyed || !this.active || this.overview || this.scope !== 'behavior'
      || this.ownerViews.focusSuspended() || !this.reviewSetupOrigin || this.reviewSetupOrigin.buffer !== this.routeBuffer
      || this.reviewSetupOrigin.owner !== this.ownerKey()) return;
    this.reviewSetupOrigin.generation = ++this.reviewSetupGeneration;
    this.reviewSetupOrigin.openedContext = undefined;
    this.reviewSetupDialogError = '';
    if (!this.reviewSetupDialogModel) {
      this.reviewSetupDialogOpen = false;
      const buffer = this.routeBuffer, context = this.reviewSetupDialogContext(), request = ++this.routeFocusRequest;
      const focused = this.element.nativeElement.ownerDocument.activeElement;
      setTimeout(() => {
        const host = this.element.nativeElement, active = host.ownerDocument.activeElement;
        if (this.destroyed || !this.active || this.overview || this.scope !== 'behavior' || this.ownerViews.focusSuspended()
          || request !== this.routeFocusRequest || buffer !== this.routeBuffer || context !== this.reviewSetupDialogContext()
          || !host.isConnected || host.closest('[hidden],[inert]')) return;
        if (active !== focused && active !== host.ownerDocument.body && active !== host.ownerDocument.documentElement
          && active?.isConnected && !active.closest('[hidden],[inert]')) return;
        host.querySelector<HTMLButtonElement>('[data-review-setup-unavailable] button')?.focus();
      });
      return;
    }
    this.reviewSetupDialogOpen = true;
  }
  changeReviewSetupRoute(event: { context: string; code: string; movement?: 'forward' | 'return'; destinationNodeId?: string }): void {
    if (!this.reviewSetupDialogOpen || !this.reviewSetupOwnerCurrent() || event.context !== this.reviewSetupDialogContext()
      || this.reviewSetupLease?.context !== event.context || this.routePendingState(this.currentDocument()) === 'stale') {
      this.reviewSetupDialogError = 'This setup changed. Its input is retained; review the current source or discard it.'; return;
    }
    const command = this.reviewSetupCommand();
    if (!command || this.routeBuffer!.command.outcomes.filter(outcome => outcome.code === event.code).length !== 1) return;
    if (event.movement !== undefined) {
      if (event.movement !== 'forward' && event.movement !== 'return') return;
      this.setReturn(command, event.code, event.movement === 'return');
    }
    if (event.destinationNodeId !== undefined) {
      const eligible = this.outcomeDestinations(command, event.code);
      if (event.destinationNodeId && eligible.filter(node => node.id === event.destinationNodeId).length !== 1) {
        this.reviewSetupDialogError = 'Choose a current destination for this path.'; return;
      }
      this.updateOutcome(command, event.code, { destinationNodeId: event.destinationNodeId });
    }
    this.reviewSetupDialogError = ''; this.pendingChangesChange.emit(true);
  }
  applyReviewSetupDialog(event: { context: string }): void {
    if (!this.reviewSetupDialogOpen || !this.reviewSetupOwnerCurrent() || event.context !== this.reviewSetupDialogContext()
      || this.reviewSetupLease?.context !== event.context || !this.reviewSetupDialogModel?.canApply) {
      this.reviewSetupDialogError = 'Review both paths and the current setup before adding it. No setup has been applied.'; return;
    }
    const command = this.reviewSetupCommand();
    if (!command) return;
    if (this.applyOutcomes(command)) {
      this.reviewSetupAppliedFocus = { context: event.context, owner: this.ownerKey(), document: JSON.stringify(this.currentDocument()), commandId: commandIdentity(command) };
      this.reviewSetupDialogOpen = false;
    } else this.reviewSetupDialogError = this.routeError || this.error || 'The setup could not be applied. Its input is retained.';
  }
  /** Inactive rendering keeps the display token; active rendering and gestures stay fresh. */
  reviewLibraryRenderContext(): string {
    return this.active ? this.reviewLibraryContext() : this.reviewLibrarySnapshot;
  }
  reviewLibraryContext(): string {
    return JSON.stringify([this.actionLibraryContext(), this.selection, this.formActionSettings]);
  }
  private reviewSetupCatalog(): string { return JSON.stringify([this.catalog, this.capabilities]); }
  private preparedPreparation(reference: StepCommand['capability']): WorkflowDefinitionDocument {
    if (!this.catalog) throw new Error('Reload installed forms before choosing Preparation.');
    const attached = this.node.declaration?.behavior.commands.filter(command => isPreparationReference(command.capability)) ?? [];
    return attached.length === 1 && attached[0].capability.key.version === 1 && reference.key.version === 2
      ? upgradePreparation(this.document, this.node.id, this.catalog, this.capabilities, reference)
      : addPreparation(this.document, this.node.id, this.catalog, this.capabilities, reference);
  }
  private preparedDirectRequest(reference: StepCommand['capability']): WorkflowDefinitionDocument {
    if (!this.catalog) throw new Error('Reload installed forms before choosing a review request.');
    const attached = this.node.declaration?.behavior.commands.filter(command => isDirectReviewRequestReference(command.capability)) ?? [];
    return attached.length === 1 && attached[0].capability.key.version === 1 && reference.key.version === 2
      ? upgradeDirectReviewRequest(this.document, this.node.id, this.catalog, this.capabilities, reference)
      : addDirectReviewRequest(this.document, this.node.id, this.catalog, this.capabilities, reference);
  }
  private preparedProvidedForm(kind: 'preparation' | 'direct-request', reference: StepCommand['capability']): WorkflowDefinitionDocument {
    return kind === 'preparation' ? this.preparedPreparation(reference) : this.preparedDirectRequest(reference);
  }
  private stageProvidedForm(kind: 'preparation' | 'direct-request', reference: StepCommand['capability']): void {
    if (this.disabled || this.loading || this.catalogError || !this.active || this.overview || this.scope !== 'work'
      || this.ownerViews.focusSuspended() || !this.adoptCurrentDocument() || !this.applyPendingChanges()) return;
    try {
      const candidate = this.preparedProvidedForm(kind, reference);
      this.formSetup = { kind, owner: this.ownerKey(), baseline: JSON.stringify(this.document), catalog: this.reviewSetupCatalog(),
        reference: structuredClone(reference), candidate: structuredClone(candidate) };
      this.formSetupError = ''; this.error = '';
      this.pendingChangesChange.emit(true); this.focusStagedForm();
    } catch (error) { this.error = error instanceof Error ? error.message : 'This form could not be prepared.'; }
  }
  stagedFormPendingState(document = this.currentDocument()): WorkflowEditorBufferState {
    const setup = this.formSetup;
    if (!setup) return 'clean';
    if (!this.ownerIsCurrent() || this.ownerKey() !== setup.owner || !document || JSON.stringify(document) !== setup.baseline
      || setup.catalog !== this.reviewSetupCatalog()) return 'stale';
    return this.disabled || this.loading || this.catalogError || this.scope !== 'work' || !hasEditableRouting(document) ? 'invalid' : 'valid';
  }
  preparationPendingState(document = this.currentDocument()): WorkflowEditorBufferState {
    return this.preparationSetup ? this.stagedFormPendingState(document) : 'clean';
  }
  directRequestPendingState(document = this.currentDocument()): WorkflowEditorBufferState {
    return this.directRequestSetup ? this.stagedFormPendingState(document) : 'clean';
  }
  applyPreparationForm(): boolean { return this.applyStagedForm('preparation'); }
  applyDirectRequestForm(): boolean { return this.applyStagedForm('direct-request'); }
  applyStagedForm(expected?: 'preparation' | 'direct-request'): boolean {
    const setup = this.formSetup;
    if (expected && setup?.kind !== expected) return false;
    if (!setup || !this.active || this.overview || this.ownerViews.focusSuspended() || this.stagedFormPendingState() !== 'valid') {
      if (setup) this.formSetupError = `The draft, author or installed forms changed. Discard this setup and choose the current ${this.stagedFormName} form again.`;
      this.focusStagedForm(); return false;
    }
    const applied = this.edit(() => {
      if (this.stagedFormPendingState(this.document) !== 'valid') throw new Error('The current draft changed before the form could be applied. Its setup is retained.');
      const next = this.preparedProvidedForm(setup.kind, setup.reference);
      if (JSON.stringify(next) !== JSON.stringify(setup.candidate)) throw new Error('The prepared form changed. Discard its setup and choose it again.');
      this.formSetup = null; this.formSetupError = '';
      this.selection = { kind: 'form', commandId: capabilityIdentity(setup.reference.key) };
      this.workLibraryKind = 'form'; this.formActionSettings = false;
      return next;
    }, true);
    if (!applied) { this.formSetupError = this.error; this.error = ''; this.focusStagedForm(); return false; }
    this.pendingChangesChange.emit(this.hasPendingChanges()); this.rememberOwnerView(); this.restoreOwnerFocus();
    return true;
  }
  discardPreparationForm(): void { this.discardStagedForm('preparation'); }
  discardDirectRequestForm(): void { this.discardStagedForm('direct-request'); }
  discardStagedForm(expected?: 'preparation' | 'direct-request'): void {
    if (expected && this.formSetup?.kind !== expected) return;
    this.formSetup = null; this.formSetupError = '';
    this.pendingChangesChange.emit(this.hasPendingChanges()); this.refreshSettledSource();
  }
  focusPreparationForm(): void { this.focusStagedForm('preparation'); }
  focusStagedForm(expected?: 'preparation' | 'direct-request'): void {
    if (expected && this.formSetup?.kind !== expected || this.ownerViews.focusSuspended() || !this.active || this.overview) return;
    setTimeout(() => {
      if (this.destroyed || !this.formSetup || !this.active || this.overview || this.ownerViews.focusSuspended()) return;
      const target = this.element.nativeElement.querySelector<HTMLElement>('[data-provided-form-setup]');
      if (target?.isConnected && !target.closest('[hidden],[inert]')) target.focus({ preventScroll: true });
    });
  }
  private reviewContracts(): StepAuthoringCommand[] {
    return this.catalog?.commands.filter(contract => contract.capability.key.provider === 'workflow' &&
      contract.capability.key.kind === 'command' && contract.capability.key.code === 'workflow.review.record' &&
      [1, 2].includes(contract.capability.key.version)) ?? [];
  }
  canChooseReviewForm(command: StepCommand): boolean {
    return !this.preparingReviewDecision && command.capability.key.provider === 'workflow' &&
      command.capability.key.code === 'workflow.review.record' && command.capability.key.version === 1 &&
      this.reviewContracts().some(contract => contract.capability.key.version === 2);
  }
  private preparedReview(reference: StepCommand['capability']): WorkflowDefinitionDocument {
    if (!this.catalog) throw new Error('Reload installed actions before choosing a review form.');
    const attached = this.node.declaration?.behavior.commands.filter(command =>
      command.capability.key.provider === 'workflow' && command.capability.key.code === 'workflow.review.record') ?? [];
    return attached.length === 1 && attached[0].capability.key.version === 1 && reference.key.version === 2
      ? upgradeReviewDecision(this.document, this.node.id, this.catalog, this.capabilities, reference)
      : prepareReviewDecision(this.document, this.node.id, this.catalog, this.capabilities, reference);
  }
  /** Choose exact installed behavior; catalog ordering never selects a newer version. */
  addReviewDecision(): void {
    if (this.disabled || this.loading || this.catalogError || !this.active || this.overview || !this.catalog ||
      this.actionLibraryOpen || this.formLibraryOpen || this.reviewLibraryOpen || this.ownerViews.focusSuspended() ||
      !this.adoptCurrentDocument() || (this.beforeSelectionChange && !this.beforeSelectionChange()) || !this.applyPendingChanges()) return;
    const active = this.element.nativeElement.ownerDocument.activeElement;
    this.reviewSetupReturnFocus = active instanceof HTMLElement ? active : null;
    this.reviewSetupReturnKind = this.commands.some(command => command.capability.key.provider === 'workflow' && command.capability.key.code === 'workflow.review.record') ? 'change' : 'add';
    const choices = this.reviewContracts();
    if (choices.length === 1 && choices[0].capability.key.version === 1) {
      this.stageReviewDecision(choices[0].capability);
      return;
    }
    this.reviewLibraryEntries = choices.map(contract => {
      const current = this.node.declaration?.behavior.commands.some(command => sameCapabilityReference(command.capability, contract.capability));
      let reason = current ? 'This step already uses this review form.' : '';
      if (!reason) {
        try { this.preparedReview(contract.capability); }
        catch (error) { reason = error instanceof Error ? error.message : 'This review form is unavailable for this step.'; }
      }
      return { id: JSON.stringify(contract.capability), categoryId: 'review', icon: 'pi pi-file',
        label: contract.capability.key.version === 2 ? 'Review decision with lookup choices' : 'Review decision',
        description: contract.capability.key.version === 2
          ? 'Review results and notes up to 4,000 characters. Enable a lookup source for notes in Forms.'
          : 'Review results and written notes using the established review form.',
        actionLabel: current ? 'Current form' : this.hasOutcomes ? 'Prepare form change' : 'Choose form',
        disabledReason: reason || undefined };
    });
    this.reviewLibraryDestination = null;
    this.reviewLibrarySnapshot = this.reviewLibraryContext();
    this.reviewLibraryOpen = true;
  }
  reviewLibraryOpened(): void { this.reviewLibraryRelease ??= this.ownerViews.suspendFocus(); }
  chooseReviewLibrary(id: string): void {
    if (!this.reviewLibraryOpen || this.disabled || this.loading || this.catalogError || !this.active || this.overview ||
      this.reviewLibrarySnapshot !== this.reviewLibraryContext() || !this.adoptCurrentDocument()) return;
    const entries = this.reviewLibraryEntries.filter(entry => entry.id === id && !entry.disabledReason);
    const choices = this.reviewContracts().filter(contract => JSON.stringify(contract.capability) === id);
    if (entries.length !== 1 || choices.length !== 1) return;
    this.reviewLibraryDestination = structuredClone(choices[0].capability);
    this.reviewLibraryOpen = false;
  }
  reviewLibraryClosed(reason: WorkflowLibraryClosedReason): void {
    this.reviewLibraryRelease?.(); this.reviewLibraryRelease = null;
    const destination = this.reviewLibraryDestination;
    this.reviewLibraryDestination = null; this.reviewLibraryOpen = false;
    if (this.destroyed || reason !== 'input' || !destination || !this.active || this.overview || this.disabled || this.loading ||
      this.reviewLibrarySnapshot !== this.reviewLibraryContext() || !this.adoptCurrentDocument()) return;
    this.stageReviewDecision(destination);
  }
  /** Prepare locally; the exact form change and retained paths apply together. */
  private stageReviewDecision(reference: StepCommand['capability']): void {
    if (!this.catalog || !this.applyPendingChanges()) return;
    try {
      const hadReview = this.node.declaration?.behavior.commands.some(command =>
        command.capability.key.provider === 'workflow' && command.capability.key.code === 'workflow.review.record');
      const setup = this.preparedReview(reference);
      const command = setup.nodes
        .find((node) => node.id === this.node.id)!
        .declaration!.behavior.commands.find(
          (command) =>
            sameCapabilityReference(command.capability, reference),
        )!;
      const previous = nearestPreviousWorkSteps(this.document, this.node.id);
      const next = this.nextDestination;
      const returns = hadReview ? this.returnCodes(command) : new Set<string>();
      const installed = this.catalog.commands.find(contract => sameCapabilityReference(contract.capability, reference))!;
      const outcomes = hadReview ? readStepOutcomeRoutes(setup, this.node.id, command, installed) : command.outcomes.map((outcome) => {
        if (outcome.code === 'changes_required' && previous.length === 1) {
          returns.add(outcome.code);
          return { ...outcome, destinationNodeId: previous[0].id };
        }
        return {
          ...outcome,
          destinationNodeId: outcome.code === 'approved' ? next : '',
        };
      });
      const workflowStatuses = this.readOutcomeWorkflowStatuses(command);
      this.routeBuffer = {
        context: this.documentContext(this.document),
        nodeType: this.node.typeCode,
        commandId: commandIdentity(command),
        original: JSON.stringify(command),
        originalDeclaration: JSON.stringify(this.node.declaration ?? null),
        originalPaths: this.routingFingerprint(),
        originalWorkflowStatuses: JSON.stringify(
          this.document.statusConfiguration ?? null,
        ),
        workflowStatuses,
        setupDocument: setup,
        setupCatalog: this.reviewSetupCatalog(),
        immediateEligible: false,
        command: { ...structuredClone(command), outcomes },
        returns,
      };
      this.selection = {
        kind: 'outcomes',
        commandId: commandIdentity(command),
      };
      this.selectedRoutePin = structuredClone(command.capability);
      this.reconcileOutcomeSelection();
      this.reviewSetupOrigin = { buffer: this.routeBuffer, baseline: this.document, owner: this.ownerKey(), generation: ++this.reviewSetupGeneration,
        returnFocus: this.reviewSetupReturnFocus, returnKind: this.reviewSetupReturnKind };
      this.reviewSetupDialogError = ''; this.reviewSetupDialogOpen = true;
      this.error = '';
      this.routeError = '';
      this.pendingChangesChange.emit(true);
    } catch (error) {
      this.error =
        error instanceof Error
          ? error.message
          : 'The review decision could not be prepared.';
    }
  }
  private routingFingerprint(document = this.document): string {
    const outgoing = document.edges.filter(
      (edge) => edge.fromNodeId === this.node.id,
    );
    const routers = document.nodes.filter(
      (node) =>
        node.typeCode === 'routing_decision' &&
        outgoing.some((edge) => edge.toNodeId === node.id),
    );
    return JSON.stringify([
      outgoing,
      routers,
      document.edges.filter((edge) =>
        routers.some(
          (node) => node.id === edge.fromNodeId || node.id === edge.toNodeId,
        ),
      ),
    ]);
  }
  private routingBase(document = this.document): WorkflowDefinitionDocument {
    const setup = this.routeBuffer?.setupDocument;
    if (!setup) return document;
    const references = [...(document.capabilityReferences ?? [])];
    for (const reference of setup.capabilityReferences ?? []) {
      const existing = references.filter(
        (item) =>
          capabilityIdentity(item.key) === capabilityIdentity(reference.key),
      );
      if (
        existing.length > 1 ||
        existing.some((item) => !sameCapabilityReference(item, reference))
      )
        throw new Error(
          'An installed action reference changed. Discard this review setup and add it again.',
        );
      if (!existing.length) references.push(reference);
    }
    return {
      ...document,
      payloadSchemaVersion: requireWorkflowPayloadVersion(
        document.payloadSchemaVersion,
        setup.payloadSchemaVersion,
      ),
      capabilityReferences: references,
      nodes: document.nodes.map((node) =>
        node.id === this.node.id
          ? {
              ...node,
              declaration: setup.nodes.find(
                (prepared) => prepared.id === node.id,
              )!.declaration,
            }
          : node,
      ),
    };
  }
  get previousStepChoices(): WorkflowDefinitionNode[] {
    try {
      return nearestPreviousWorkSteps(this.document, this.node.id);
    } catch {
      return [];
    }
  }
  returnToPrevious(command: StepCommand, code: string): void {
    if (this.disabled || this.loading) return;
    const buffer = this.bufferOutcomes(command);
    if (!buffer) return;
    const previous = this.previousStepChoices;
    if (!previous.length) return;
    buffer.returns.add(code);
    this.updateOutcome(command, code, {
      destinationNodeId: previous.length === 1 ? previous[0].id : '',
    });
    if (previous.length !== 1) this.focusPendingChanges();
  }
  outcomeReturns(command: StepCommand): Set<string> {
    return this.routeBuffer?.commandId === commandIdentity(command)
      ? this.routeBuffer.returns
      : this.returnCodes(command);
  }
  destinationLabel(id: string): string {
    return routeDestinationPresentation(
      this.document,
      this.node.id,
      id,
      'forward',
    ).label;
  }
  outcomeDestination(
    command: StepCommand,
    outcome: StepCommand['outcomes'][number],
  ) {
    return routeDestinationPresentation(
      this.document,
      this.node.id,
      outcome.destinationNodeId,
      this.outcomeReturns(command).has(outcome.code) ? 'return' : 'forward',
      this.outcomeDestinations(command, outcome.code).map(
        (target) => target.id,
      ),
    );
  }
  pendingSavedPath(command: StepCommand, code: string): string {
    return this.routeBuffer?.commandId === commandIdentity(command) ? this.routeBuffer.savedRouteSummaries?.[code] ?? '' : '';
  }
  get completionTrigger(): string {
    const complete = this.commands.find((command) =>
      this.isFinalCompletion(command),
    );
    if (complete) return this.label(complete);
    const completing = this.commands.filter(
      (command) => this.contract(command)?.completesStep !== false,
    );
    return completing.length === 1
      ? this.label(completing[0])
      : 'Step completion';
  }
  get hasSavedEvidenceActions(): boolean {
    return this.commands.some(
      (command) => this.contract(command)?.completesStep === false,
    );
  }
  outcomeSummary(command: StepCommand): string {
    const outcomes = this.outcomes(command);
    return outcomes
      .map(
        (outcome) =>
          `${outcome.label}: ${this.destinationLabel(outcome.destinationNodeId)}${this.outcomeReturns(command).has(outcome.code) ? ' (return)' : ''}`,
      )
      .join(' · ');
  }
  routeIssue(command: StepCommand): string {
    if (this.routeBuffer?.commandId === commandIdentity(command))
      return this.routeError || 'Unapplied outcome changes';
    const contract = this.contract(command);
    if (!contract) return 'Module unavailable';
    try {
      const outcomes = this.projectedOutcomes(command);
      if (outcomes.some((outcome) => !outcome.destinationNodeId))
        return 'Choose destinations';
      const routed = applyComponentRoutes(
        this.document,
        this.node.id,
        { ...command, outcomes },
        contract,
        this.returnCodes(command),
      );
      if (!this.sameRouteEdges(routed.edges, this.document.edges))
        return 'Destinations need to be applied';
    } catch (error) {
      return error instanceof Error ? error.message : 'Inspect workflow paths';
    }
    return '';
  }
  /** Compare validated projections without requiring the writer's edge-array order. */
  private sameRouteEdges(left: WorkflowDefinitionDocument['edges'], right: WorkflowDefinitionDocument['edges']): boolean {
    if (!Array.isArray(left) || !Array.isArray(right) || left.length !== right.length) return false;
    const byIdentity = new Map<string, WorkflowDefinitionDocument['edges'][number]>();
    for (const edge of right) {
      if (!edge || Array.isArray(edge) || typeof edge.id !== 'string' || !edge.id.trim() || byIdentity.has(edge.id)) return false;
      byIdentity.set(edge.id, edge);
    }
    const seen = new Set<string>();
    const comparison = (edge: WorkflowDefinitionDocument['edges'][number]) => {
      const value = { ...edge };
      if (value.outcomeCode == null) delete value.outcomeCode;
      if (value.pathKindCode == null) delete value.pathKindCode;
      return value;
    };
    return left.every(edge => {
      if (!edge || Array.isArray(edge) || typeof edge.id !== 'string' || !edge.id.trim() || seen.has(edge.id)) return false;
      seen.add(edge.id);
      const other = byIdentity.get(edge.id);
      return !!other && sameSavedRouteValue(comparison(edge), comparison(other));
    });
  }
  routeInspectionIssue(command: StepCommand): string {
    const contract = this.contract(command);
    if (!contract) return '';
    try {
      readStepOutcomeRoutes(this.routingBase(), this.node.id, command, contract);
      return '';
    } catch (error) {
      return error instanceof Error ? error.message : 'Inspect workflow paths.';
    }
  }
  actionSummary(command: StepCommand): string {
    const count = command.requirements.length;
    const contract = this.contract(command);
    return [
      contract?.exclusiveCompletion ? 'Completes step' : '',
      count ? `${count} required check${count === 1 ? '' : 's'}` : '',
      contract?.form && !this.isFinalCompletion(command) ? 'Form attached' : '',
    ]
      .filter(Boolean)
      .join(' · ');
  }
  isFinalCompletion(command: StepCommand): boolean {
    return workflowCompleteCommandAdapter.matches(command.capability);
  }
  get formCommands(): StepCommand[] {
    return this.commands.filter(
      (command) =>
        !this.isFinalCompletion(command) &&
        !isBlockOwnedCommand(command.capability.key),
    );
  }
  private isFormContract(contract: StepAuthoringCommand | undefined): boolean {
    return !!(contract?.form || contract?.supportsCaptureForm);
  }
  get workFormCommands(): StepCommand[] {
    return this.formCommands.filter(
      (command) =>
        !!command.captureForm ||
        !!command.formConfiguration ||
        this.isFormContract(this.contract(command)) ||
        this.isMainForm(command),
    );
  }
  get workActionCommands(): StepCommand[] {
    const forms = new Set(this.workFormCommands.map(commandIdentity));
    return this.commands.filter(
      (command) =>
        !isBlockOwnedCommand(command.capability.key) &&
        !forms.has(commandIdentity(command)),
    );
  }
  get workOverviewCountsKnown(): boolean {
    return !!this.catalog && !this.loading && [...this.workFormCommands, ...this.workActionCommands].every(command => !!this.workOverviewContract(command));
  }
  private workOverviewContract(command: StepCommand): StepAuthoringCommand | undefined {
    return this.exactActionContract(command);
  }
  workOverviewLabel(command: StepCommand): string {
    return this.workOverviewContract(command)?.label ?? command.capability.key.code;
  }
  workOverviewFormSummary(command: StepCommand): string {
    if (!this.workOverviewContract(command)) return 'Matching module unavailable or ambiguous · saved form preserved.';
    return command.capability.key.provider === 'workflow' && command.capability.key.code === 'workflow.form.save' && command.capability.key.version === 1
      ? 'Save form · Saves answers; step stays open.' : this.formFieldSummary(command);
  }
  workOverviewActionSummary(command: StepCommand): string {
    if (!this.workOverviewContract(command)) return 'Matching module unavailable or ambiguous · saved command preserved; its form or action type needs review.';
    return this.isFinalCompletion(command) ? 'Checks requirements, then uses the configured route.' : this.actionSummary(command);
  }
  get libraryCommands(): StepAuthoringCommand[] {
    if (this.scope !== 'work') return this.availableCommands;
    return this.libraryChoices
      .filter((choice) => !choice.reason)
      .map((choice) => choice.command);
  }
  readonly trackLibraryChoice = (
    _index: number,
    choice: { command: StepAuthoringCommand; reason: string },
  ) => commandIdentity(choice.command);
  get libraryChoices(): { command: StepAuthoringCommand; reason: string }[] {
    if (this.scope !== 'work')
      return this.availableCommands.map((command) => ({
        command,
        reason: this.incompatibility(command),
      }));
    const family = (command: { capability: StepCommand['capability'] }) =>
      `${command.capability.key.provider}/${command.capability.key.code}`;
    const attached = new Set(this.commands.map(family));
    const preferred = new Map<
      string,
      { command: StepAuthoringCommand; reason: string }
    >();
    for (const command of this.availableCommands) {
      if (
        attached.has(family(command)) ||
        (this.workLibraryKind === 'form'
          ? !this.isFormContract(command)
          : command.supportsCaptureForm === true)
      )
        continue;
      let reason = this.catalog?.commands.filter(other => commandIdentity(other) === commandIdentity(command)).length !== 1
        ? 'This action identity has more than one installed contract. Reload the library before choosing it.' : this.incompatibility(command);
      try {
        // Use the same exact contract checks as Add, on a disposable document.
        if (!reason)
          addStepCommand(this.document, this.node.id, command, this.capabilities);
      } catch (error) {
        reason = error instanceof Error
          ? error.message
          : 'This action is unavailable for this step.';
      }
      const previous = preferred.get(family(command));
      if (
        !previous ||
        (!reason && !!previous.reason) ||
        (!!reason === !!previous.reason &&
          command.capability.key.version > previous.command.capability.key.version)
      )
        preferred.set(family(command), { command, reason });
    }
    return [...preferred.values()];
  }
  formFieldSummary(command: StepCommand): string {
    const count = this.contract(command)?.supportsCaptureForm
      ? (command.captureForm?.fields?.length ?? 0)
      : (this.contract(command)?.formFields?.length ?? 0);
    return `${count} field${count === 1 ? '' : 's'}`;
  }
  formSummary(command: StepCommand): string {
    const count = this.contract(command)?.supportsCaptureForm
      ? (command.captureForm?.fields?.length ?? 0)
      : (this.contract(command)?.formFields?.length ?? 0);
    return `${command.captureForm?.title || this.interactions(command)[0]?.label || 'Action form'} · ${count} field${count === 1 ? '' : 's'}`;
  }
  incompatibility(contract: StepAuthoringCommand): string {
    const unavailable = this.commandAuthoringReason(contract);
    if (unavailable) return unavailable;
    if (
      this.node.declaration?.behavior.completionRequirements?.length &&
      contract.completesStep !== false &&
      contract.supportsCompletionRequirements !== true
    )
      return 'This step completes through its requirements. Add a save-only action, or remove the completion requirements before adding an action that advances immediately.';
    if (
      this.commands.length &&
      (contract.exclusiveCompletion ||
        this.commands.some(
          (command) => this.contract(command)?.exclusiveCompletion,
        ))
    )
      return 'This action completes the step on its own. Use a separate step for other completion actions.';
    if (contract.supportsCaptureForm && contract.completesStep !== false) {
      if (
        this.catalog?.interactions.filter((item) =>
          sameCapabilityReference(item.command, contract.capability),
        ).length !== 1
      )
        return 'This form needs one matching installed detail interaction. Reload components before adding it.';
    }
    return '';
  }
  readonly identity = commandIdentity;
  readonly isRestrictedProvidedLookup = (reference: StepCommand['capability']) =>
    isPreparationLookupCommand(reference) || isReviewRequestLookupCommand(reference);
  private providedFormName(reference: StepCommand['capability']): string {
    return isReviewRequestLookupCommand(reference) ? 'Review request' : 'Preparation';
  }
  trackCommand(_: number, command: StepCommand) {
    return commandIdentity(command);
  }
  get commands() {
    return (
      (
        this.routeBuffer?.setupDocument?.nodes.find(
          (node) => node.id === this.node.id,
        ) ?? this.node
      ).declaration?.behavior.commands ?? []
    );
  }
  get availableCommands() {
    return (
      this.catalog?.commands.filter(
        (contract) =>
          !isBlockOwnedCommand(contract.capability.key) &&
          !(
            contract.capability.key.provider === 'workflow' &&
            [
              ...(this.scope === 'work' ? [] : ['workflow.form.save']),
              'workflow.review.record',
              'workflow.step.complete',
            ].includes(contract.capability.key.code)
          ) &&
          !this.commands.some(
            (command) => commandIdentity(command) === commandIdentity(contract),
          ),
      ) ?? []
    );
  }
  get destinations() {
    return this.document.nodes.filter((node) =>
      ['human_step', 'terminal'].includes(node.typeCode),
    );
  }
  get forwardDestinations() {
    return eligibleStepDestinations(this.document, this.node.id, 'forward');
  }
  get routedCommands() {
    return this.commands.filter((command) => this.canRoute(command));
  }
  outcomeDestinations(command: StepCommand, code: string) {
    return eligibleStepDestinations(
      this.document,
      this.node.id,
      this.outcomeReturns(command).has(code) ? 'return' : 'forward',
    );
  }
  hasUnavailableDestination(
    command: StepCommand,
    outcome: StepCommand['outcomes'][number],
  ): boolean {
    return (
      !!outcome.destinationNodeId &&
      !this.outcomeDestinations(command, outcome.code).some(
        (target) => target.id === outcome.destinationNodeId,
      )
    );
  }
  unavailableDestinationLabel(id: string): string {
    const target = this.document.nodes.filter((node) => node.id === id);
    return target.length === 1
      ? `${target[0].displayName || target[0].stepCode || id} (unavailable for this movement)`
      : `Missing or ambiguous destination (${id})`;
  }
  get unavailableNextDestination(): boolean {
    return (
      !!this.nextDestination &&
      !this.forwardDestinations.some(
        (target) => target.id === this.nextDestination,
      )
    );
  }
  get nextDestination() {
    return this.nextRoute.state === 'direct'
      ? this.document.edges.find((edge) => edge.fromNodeId === this.node.id)!
          .toNodeId
      : '';
  }
  get nextRoute() {
    return directRoutePresentation(
      this.document,
      this.node.id,
      this.forwardDestinations.map((target) => target.id),
    );
  }
  get nextPathSummary(): string {
    const paths = this.document.edges.filter(
      (edge) => edge.fromNodeId === this.node.id,
    );
    return paths.length
      ? paths
          .map((edge) =>
            this.document.nodes.some((node) => node.id === edge.toNodeId)
              ? this.destinationLabel(edge.toNodeId)
              : `Missing destination (${edge.toNodeId})`,
          )
          .join(' · ')
      : 'Choose destination';
  }
  get nextPathIssue(): string {
    return this.nextRoute.state === 'direct' ? '' : this.nextRoute.sentence;
  }
  get hasOutcomes() {
    return this.routedCommands.length > 0;
  }
  contract(command: StepCommand) {
    return this.catalog?.commands.find((contract) =>
      sameCapabilityReference(contract.capability, command.capability),
    );
  }
  exactActionContract(command: StepCommand): StepAuthoringCommand | undefined {
    const matches = this.catalog?.commands.filter(contract => commandIdentity(contract) === commandIdentity(command)) ?? [];
    return matches.length === 1 && sameCapabilityReference(matches[0].capability, command.capability) ? matches[0] : undefined;
  }
  label(command: StepCommand) {
    return this.contract(command)?.label ?? command.capability.key.code;
  }
  commandAuthoringReason(command: Pick<StepCommand, 'capability'>): string {
    if (this.isRestrictedProvidedLookup(command.capability)) {
      const name = this.providedFormName(command.capability);
      if (this.disabled || this.loading || !this.active || this.overview || this.scope !== 'work' || this.ownerViews.focusSuspended())
        return `This ${name} form is read-only in the current editor. Its saved settings are preserved.`;
      const current = this.currentProvidedFormAuthoringContext();
      if (current && sameCapabilityReference(current.command.capability, command.capability))
        return isReviewRequestLookupCommand(command.capability)
          ? reviewRequestLookupSettingsAuthoringReason(current, current.command, current.installed)
          : preparationLookupSettingsAuthoringReason(current, current.command, current.installed);
    }
    return reviewRequestLookupAuthoringReason(command.capability) || preparationLookupAuthoringReason(command.capability);
  }

  private documentContext(document: WorkflowDefinitionDocument): string {
    return JSON.stringify([
      document.workflowCode,
      document.definitionVersionCode,
      document.packReference,
    ]);
  }
  ngOnChanges(changes: SimpleChanges): void {
    this.externalWorkflowStatusesView = this.externalWorkflowStatuses ? this.workflowStatusesView?.() ?? null : null;
    if (changes['document'] || changes['node'] || changes['ownerContext'] || !this.navigationContext) {
      this.navigationOwner = this.ownerKey(); this.navigationContext = this.documentContext(this.document);
    }
    if (!this.hasPendingChanges() && (changes['document'] || changes['node'] || changes['ownerContext'] || !this.inputContext)) {
      this.inputOwner = this.ownerKey(); this.inputContext = this.documentContext(this.document);
    }
    const baseViewKey = this.ownerViews.key(this.document, this.node.id, `components:${this.scope}`);
    const viewKey = this.scope === 'behavior' ? JSON.stringify([baseViewKey, this.formViewContext?.() ?? this.ownerContext?.() ?? null]) : baseViewKey;
    const ownerChanged = viewKey !== this.ownerViewKey;
    if (ownerChanged || changes['active']) this.ownerFocusRequest++;
    const retainReset = this.scope === 'work' && !!changes['resetVersion'] && !changes['resetVersion'].firstChange;
    if (ownerChanged || retainReset || changes['active']?.currentValue === false) this.rememberOwnerView();
    if (ownerChanged) {
      this.ownerViewKey = viewKey;
      this.ownerFocus = null;
      this.ownerFocusSelection = '';
      this.routeOutcomeSelections.clear();
      this.selectedRoutePin = null;
    }
    const nodeChanged = this.node.id !== this.selectedNodeId,
      documentContext = this.documentContext(this.document),
      contextChanged = documentContext !== this.selectedDocumentContext;
    this.selectedDocumentContext = documentContext;
    if (
      nodeChanged ||
      contextChanged ||
      this.scope !== 'behavior' ||
      !this.immediateRoutingChanges
    )
      this.workflowStatusesOpen = false;
    if (
      changes['resetVersion'] &&
      (!changes['resetVersion'].firstChange || !!this.selectedNodeId)
    ) {
      const selectedCommandId = this.selection.commandId, selectedOutcomeCode = this.selection.outcomeCode;
      this.resetPendingChanges();
      this.lastFocusAction = this.focusAction;
      this.lastFocusForm = this.focusForm;
      const routed =
        this.scope === 'behavior' && !this.inspectorOnly
          ? this.routedCommands.find(
              (command) => commandIdentity(command) === selectedCommandId,
            ) ?? this.routedCommands[0]
          : undefined;
      this.selection = routed
        ? { kind: 'outcomes', commandId: commandIdentity(routed), outcomeCode: commandIdentity(routed) === selectedCommandId ? selectedOutcomeCode : undefined }
        : { kind: 'next' };
    }
    if (nodeChanged) {
      this.formActionSettings = false; this.initializedFormSettings.clear();
      this.selectedNodeId = this.node.id;
      this.workflowStatusesOpen = false;
      this.resetPendingChanges();
      const routed = this.routedCommands[0];
      this.selection = this.inspectorOnly
        ? { kind: 'library' }
        : routed
          ? { kind: 'outcomes', commandId: commandIdentity(routed) }
          : { kind: 'next' };
    }
    if (
      this.inspectorOnly &&
      (nodeChanged || changes['actionIdentity'] || changes['inspectorOnly'])
    ) {
      this.selection = this.actionIdentity
        ? { kind: 'action', commandId: this.actionIdentity }
        : { kind: 'library' };
    }
    if (this.selection.commandId && !this.selectedCommand) {
      this.formActionSettings = false;
      this.selection = this.inspectorOnly
        ? { kind: 'library' }
        : { kind: 'next' };
    }
    const key = JSON.stringify([
      this.document.workflowCode,
      this.document.packReference,
    ]);
    if (this.componentCatalog !== undefined) {
      if (key !== this.context || changes['componentCatalog']) this.request++;
      this.context = key;
      this.catalog = this.componentCatalog;
      this.capabilities = this.componentCapabilities;
      this.loading = this.catalog === null;
    } else if (key !== this.context || changes['componentCatalog']) {
      this.context = key;
      void this.load();
    }
    if (ownerChanged && this.scope === 'behavior' && !this.inspectorOnly) {
      const routed = this.routedCommands[0];
      this.selection = routed ? { kind: 'outcomes', commandId: commandIdentity(routed) } : { kind: 'next' };
    }
    if (ownerChanged || retainReset) this.restoreOwnerView();
    this.applyActionFocus();
    this.applyFormFocus();
    this.reconcileRecordedSelection();
    this.reconcileOutcomeSelection();
    this.reconcileWorkSelection();
    if (this.active && ((ownerChanged && !!this.ownerViews.read(this.ownerViewKey)) ||
      (changes['active']?.currentValue === true && !changes['active'].firstChange)) &&
      (this.scope === 'work' || this.scope === 'behavior') && !this.inspectorOnly)
      this.restoreOwnerFocus();
    this.ownerDocument = this.document;
    this.ownerNodeId = this.node.id;
  }
  private reconcileWorkSelection(): void {
    if (
      this.scope !== 'work' ||
      !this.catalog ||
      this.selection.kind !== 'next'
    )
      return;
    const form = this.workFormCommands[0];
    const action = this.workActionCommands[0];
    if (form)
      this.selection = { kind: 'form', commandId: commandIdentity(form) };
    else if (action)
      this.selection = { kind: 'action', commandId: commandIdentity(action) };
  }
  private reconcileRecordedSelection(): void {
    if (
      this.selection.kind === 'outcomes' &&
      this.selectedCommand &&
      !this.canRoute(this.selectedCommand) &&
      !this.routeBuffer
    )
      this.selection = this.inspectorOnly
        ? { kind: 'action', commandId: commandIdentity(this.selectedCommand) }
        : { kind: 'next' };
  }
  private applyActionFocus(): void {
    if (
      !this.active ||
      !this.catalog ||
      !this.focusAction ||
      this.ownerViews.hasConsumedFocus(this.ownerViewKey, this.focusAction) ||
      this.focusAction === this.lastFocusAction ||
      (this.scope === 'work' && this.focusAction.outcomes)
    )
      return;
    const commands = this.commands.filter((command) =>
      sameCapabilityReference(command.capability, this.focusAction!.capability),
    );
    if (commands.length !== 1) return;
    const command = commands[0];
    const applied =
      this.scope === 'behavior' && !this.inspectorOnly
        ? this.canRoute(command)
          ? this.selectOutcomes(command, this.focusAction.outcomeCode)
          : this.selectNext()
        : this.selectAction(command);
    if (applied) {
      this.lastFocusAction = this.focusAction;
      this.ownerViews.consumeFocus(this.ownerViewKey, this.focusAction);
    }
  }
  private applyFormFocus(): void {
    if (
      !this.active ||
      this.scope !== 'work' ||
      !this.catalog ||
      !this.focusForm ||
      this.ownerViews.hasConsumedFocus(this.ownerViewKey, this.focusForm) ||
      this.focusForm === this.lastFocusForm
    )
      return;
    const commands = this.commands.filter((command) =>
      sameCapabilityReference(command.capability, this.focusForm!.capability),
    );
    if (commands.length === 1 && this.selectForm(commands[0])) {
      this.lastFocusForm = this.focusForm;
      this.ownerViews.consumeFocus(this.ownerViewKey, this.focusForm);
    }
  }
  ngOnDestroy(): void {
    this.destroyed = true;
    this.reviewSetupLease?.release(); this.reviewSetupLease = null;
    this.actionLibraryRelease?.(); this.actionLibraryRelease = null;
    this.formLibraryRelease?.(); this.formLibraryRelease = null;
    this.reviewLibraryRelease?.(); this.reviewLibraryRelease = null;
    this.ownerFocusRequest++;
    this.routeFocusRequest++;
    this.rememberOwnerView();
    this.request++;
    this.statusFocusRequest++;
  }
  async load(): Promise<void> {
    if (this.componentCatalog !== undefined) return;
    const request = ++this.request;
    this.loading = true;
    this.error = '';
    this.catalogError = '';
    this.catalog = null;
    try {
      const [catalog, capabilities] = await Promise.all([
        firstValueFrom(this.service.getStepAuthoringCatalog(this.document)),
        firstValueFrom(this.service.getCapabilities(this.document)),
      ]);
      if (request !== this.request) return;
      this.catalog = catalog;
      this.capabilities = capabilities;
      this.capabilitiesChange.emit(capabilities);
      this.catalogChange.emit(catalog);
      this.applyActionFocus();
      this.applyFormFocus();
      this.reconcileRecordedSelection();
      this.reconcileOutcomeSelection();
      this.reconcileWorkSelection();
    } catch {
      if (request === this.request)
        this.error = this.catalogError =
          'Installed components could not be loaded. Retry to configure this step.';
    } finally {
      if (request === this.request) this.loading = false;
    }
  }
  private edit(operation: () => WorkflowDefinitionDocument, allowStagedForm = false, skipUnchanged = false): boolean {
    if (this.disabled || this.loading) return false;
    if (this.formSetup && !allowStagedForm) {
      this.formSetupError = `Apply or discard the staged ${this.stagedFormName} form before changing this work.`;
      this.focusStagedForm(); return false;
    }
    if (!this.adoptCurrentDocument()) return false;
    if (this.workEditingUnavailable) {
      this.error =
        'Enable editing for this saved workflow format before changing its work.';
      return false;
    }
    try {
      const matching = this.document.nodes.filter(
        (node) => node.id === this.node.id,
      );
      if (matching.length !== 1)
        throw new Error(
          'This step identity is ambiguous. Repair the draft before editing components.',
        );
      const identities = this.commands.map(commandIdentity);
      if (new Set(identities).size !== identities.length)
        throw new Error(
          'Duplicate action identities must be repaired before editing this step.',
        );
      const next = operation();
      if (skipUnchanged && next === this.document) return true;
      this.document = next;
      this.node = next.nodes.find((node) => node.id === this.node.id)!;
      this.error = '';
      this.message = '';
      this.documentChange.emit(next);
      return true;
    } catch (error) {
      this.error =
        error instanceof Error
          ? error.message
          : 'The component could not be changed.';
      return false;
    }
  }
  initialize(): void {
    this.edit(() => initializeBlankStep(this.document, this.node.id));
  }
  addAction(item?: StepAuthoringCommand): void {
    if (!this.adoptCurrentDocument() || !this.applyPendingChanges()) return;
    if (item) this.selectedAction = commandIdentity(item);
    const matches = this.catalog?.commands.filter(contract => commandIdentity(contract) === this.selectedAction) ?? [];
    if (matches.length !== 1 || (item && !sameCapabilityReference(matches[0].capability, item.capability)) ||
      !this.availableCommands.includes(matches[0])) { this.error = 'This exact action choice is unavailable or ambiguous. Reload the action library before adding it.'; return; }
    const contract = matches[0];
    if (this.scope === 'work' && this.commands.some(command => command.capability.key.provider === contract.capability.key.provider && command.capability.key.code === contract.capability.key.code)) {
      this.error = 'This action family is already attached. Add action does not upgrade or replace saved commands.'; return;
    }
    this.edit(() => {
      const incompatible = this.incompatibility(contract);
      if (incompatible) throw new Error(incompatible);
      const next = addStepCommand(
        this.document,
        this.node.id,
        contract,
        this.capabilities,
      );
      if (
        contract.supportsCaptureForm !== true ||
        contract.completesStep === false
      )
        return next;
      const interactions =
        this.catalog?.interactions.filter((item) =>
          sameCapabilityReference(item.command, contract.capability),
        ) ?? [];
      if (interactions.length !== 1)
        throw new Error(
          'This form needs one matching installed detail interaction. Reload components before adding it.',
        );
      const declaration = next.nodes.find(
        (node) => node.id === this.node.id,
      )!.declaration!;
      return replacePresentation(next, this.node.id, {
        ...declaration,
        detail: {
          ...declaration.detail,
          mainInteraction: {
            code: interactions[0].code,
            version: interactions[0].version,
            command: contract.capability,
          },
        },
      });
    });
    if (!this.error) {
      this.selectedAction = '';
      this.selection = {
        kind:
          this.scope === 'work' && this.isFormContract(contract)
            ? 'form'
            : 'action',
        commandId: commandIdentity(contract),
      };
      this.workLibraryKind = this.selection.kind === 'form' ? 'form' : 'action'; this.formActionSettings = false;
      this.actionSelectionChange.emit(this.selectedCommand ?? null);
    }
  }
  removeAction(command: StepCommand): void {
    if (!this.adoptCurrentDocument() || !this.applyPendingChanges()) return;
    const removed = this.edit(() => {
      command = this.exactCurrentCommand(command);
      const declaration = this.node.declaration!;
      const reason = this.removalReason(command);
      if (reason) throw new Error(reason);
      const mainInteraction = declaration.detail.mainInteraction;
      return replacePresentation(this.document, this.node.id, {
        ...declaration,
        behavior: {
          ...declaration.behavior,
          commands: this.commands.filter(
            (item) => commandIdentity(item) !== commandIdentity(command),
          ),
        },
        detail: {
          ...declaration.detail,
          mainInteraction:
            mainInteraction &&
            sameCapabilityReference(mainInteraction.command, command.capability)
              ? null
              : mainInteraction,
        },
      });
    });
    if (removed) {
      this.selection = { kind: 'library' };
      this.actionSelectionChange.emit(null);
    }
  }
  removalReason(command: StepCommand): string {
    const unavailable = this.commandAuthoringReason(command);
    if (unavailable) return unavailable;
    if (this.node.declaration?.behavior.completionRequirements?.length && (this.isFinalCompletion(command) || this.exactActionContract(command)?.supportsCompletionRequirements === true))
      return "Remove this step's completion requirements before removing Complete step.";
    if (this.node.declaration?.dashboards?.some(view => view.quickActions.some(reference => sameCapabilityReference(reference, command.capability))))
      return 'Remove this action from its dashboard quick actions first.';
    return '';
  }
  completionRequirementLabel(row: WorkflowCompletionRequirement): string {
    const providers = this.catalog?.requirementProviders?.filter(provider => provider.provider === row?.provider && provider.version === row.version) ?? [];
    if (row?.provider === 'workflow.form-completed' && row.version === 1 && row.settingsVersion === 1 && providers.length === 1 &&
      providers[0].settingsVersion === row.settingsVersion && !!providers[0].settingsHash && providers[0].settingsHash === row.settingsHash &&
      row.settings && Object.keys(row.settings).length === 1 && typeof row.settings['formId'] === 'string') {
      const forms = this.commands.filter(command => command.capability.key.provider === 'workflow' && command.capability.key.code === 'workflow.form.save' &&
        command.capability.key.version === 1 && command.captureForm?.id === row.settings['formId'] && !isUnsupportedCaptureForm(command.captureForm) && !!this.exactActionContract(command));
      if (forms.length === 1) return `${forms[0].captureForm?.title || 'Form'} saved`;
    }
    return `${row?.provider || 'Saved requirement'} v${row?.version ?? '?'} · authored binding`;
  }
  private changeCommand(
    command: StepCommand,
    update: Partial<StepCommand>,
    document = this.document,
  ): WorkflowDefinitionDocument {
    command = this.exactCurrentCommand(command, document);
    const unavailable = this.commandAuthoringReason(command);
    if (unavailable) throw new Error(unavailable);
    if (this.isRestrictedProvidedLookup(command.capability) && Object.keys(update).some(key => key !== 'formConfiguration'))
      throw new Error(`${this.providedFormName(command.capability)} input and completion behavior are provided by its installed action. Its saved settings are preserved.`);
    if (Object.hasOwn(update, 'captureForm') || Object.hasOwn(update, 'formConfiguration')) {
      const next = { ...command, ...update };
      if ([command, next].some(item => isUnsupportedFormConfiguration(item) || !isWorkflowFormVersionAvailable(document.payloadSchemaVersion, item.captureForm)
        || !isWorkflowFormVersionAvailable(document.payloadSchemaVersion, item.formConfiguration)))
        throw new Error('This saved form version is unavailable in this workflow format. Its settings are preserved.');
      if (next.captureForm && next.captureForm.version !== 1 && isUnsupportedCaptureForm(next.captureForm))
        throw new Error('This form layout has invalid row groups. Its saved fields are preserved.');
      if (next.formConfiguration?.version === 2) {
        const contract = this.exactActionContract(command);
        if (!contract || workflowFormFieldIssues(next, contract).form)
          throw new Error('This form layout is unavailable or has invalid row groups. Its saved settings are preserved.');
      }
    }
    const declaration = document.nodes.find(
      (node) => node.id === this.node.id,
    )!.declaration!;
    return replacePresentation(document, this.node.id, {
      ...declaration,
      behavior: {
        ...declaration.behavior,
        commands: declaration.behavior.commands.map((item) =>
          commandIdentity(item) === commandIdentity(command)
            ? { ...item, ...update }
            : item,
        ),
      },
    });
  }
  onDataFlowPending(pending: boolean): void {
    this.pendingChangesChange.emit(
      pending || !!this.formSetup || !!this.routeBuffer || this.workflowStatusesPending(),
    );
  }
  applyDataFlow(command: StepCommand, patch: Partial<StepCommand>): void {
    if (this.isRestrictedProvidedLookup(command.capability)) {
      this.error = `${this.providedFormName(command.capability)} input connections are provided by its installed action. Its saved settings are preserved.`;
      return;
    }
    this.edit(() => this.changeCommand(command, patch));
  }
  missingRequiredChecks(command: StepCommand) {
    return (
      this.exactActionContract(command)?.requirements.filter(
        (rule) => rule.mandatory && !command.requirements.includes(rule.code),
      ) ?? []
    );
  }
  restoreRequiredChecks(command: StepCommand): void {
    if (this.disabled || this.loading || !this.applyPendingChanges()) return;
    const current = this.commands.find((item) =>
      sameCapabilityReference(item.capability, command.capability),
    );
    if (!current) return;
    const missing = this.missingRequiredChecks(current);
    if (!missing.length) return;
    this.edit(() =>
      this.changeCommand(current, {
        requirements: [
          ...current.requirements,
          ...missing.map((rule) => rule.code),
        ],
      }),
    );
  }
  setRequirement(command: StepCommand, code: string, checked: boolean): void {
    if (!this.adoptCurrentDocument()) return;
    try { command = this.exactCurrentCommand(command); } catch (error) { this.error = error instanceof Error ? error.message : 'Select this action again.'; return; }
    const rule = this.exactActionContract(command)?.requirements.find(
      (item) => item.code === code,
    );
    if (!rule || rule.mandatory) return;
    if (!this.applyPendingChanges()) return;
    command = this.commands.find(item => sameCapabilityReference(item.capability, command.capability))!;
    this.edit(() =>
      this.changeCommand(command, {
        requirements: checked
          ? [...new Set([...command.requirements, code])]
          : command.requirements.filter((item) => item !== code),
      }),
    );
  }
  get captureFormCopies(): WorkflowCaptureFormCopy[] {
    return this.document.nodes
      .filter((node) => node.id !== this.node.id)
      .flatMap((node) =>
        (node.declaration?.behavior.commands ?? [])
          .filter((command) => command.captureForm != null)
          .map((command) => ({
            key: JSON.stringify([node.id, commandIdentity(command)]),
            label: `${node.displayName || node.stepCode || node.id} · ${command.captureForm!.title || 'Untitled form'}`,
            form: command.captureForm!,
          })),
      );
  }
  formVersionUnavailable(command: StepCommand): boolean {
    const document = this.currentDocument() ?? this.document;
    return isUnsupportedFormConfiguration(command) || !isWorkflowFormVersionAvailable(document.payloadSchemaVersion, command.captureForm)
      || !isWorkflowFormVersionAvailable(document.payloadSchemaVersion, command.formConfiguration);
  }
  formLayoutEnableReason(command: StepCommand): string {
    return formLayoutEnableReason(this.currentDocument() ?? this.document, command, this.exactActionContract(command), this.catalog, this.node.id);
  }
  changeFormLayout(command: StepCommand, fieldId: string, change: Partial<WorkflowFormLayout>): void {
    if (!this.active || this.overview || this.scope !== 'work' || this.formActionSettings || this.formSetup
      || this.selection.kind !== 'form' || this.selection.commandId !== commandIdentity(command)
      || this.formLibraryOpen || this.actionLibraryOpen || this.reviewLibraryOpen || this.ownerViews.focusSuspended()
      || !this.adoptCurrentDocument() || !this.applyPendingChanges()) return;
    this.edit(() => {
      const current = this.exactCurrentCommand(command);
      const installed = this.exactActionContract(current);
      if (!installed) throw new Error('The exact installed form is unavailable. Its saved settings are preserved.');
      const unavailable = this.commandAuthoringReason(current);
      if (unavailable) throw new Error(unavailable);
      return changeFormFieldLayout(this.document, this.node.id, current, installed, fieldId, change, this.catalog);
    }, false, true);
  }
  enableFormLayout(command: StepCommand): void {
    if (command.captureForm && command.captureForm.version !== 1 || !command.captureForm && command.formConfiguration?.version === 2) return;
    if (!this.active || this.overview || this.scope !== 'work' || this.formActionSettings || this.formLibraryOpen || this.actionLibraryOpen || this.reviewLibraryOpen || this.ownerViews.focusSuspended()
      || !this.adoptCurrentDocument() || !this.applyPendingChanges()) return;
    this.edit(() => {
      command = this.exactCurrentCommand(command);
      const installed = this.exactActionContract(command);
      if (!installed) throw new Error('The exact installed action is unavailable. Its saved form is preserved.');
      if (this.isRestrictedProvidedLookup(command.capability)) {
        if (this.selection.kind !== 'form' || this.selection.commandId !== commandIdentity(command))
          throw new Error(`Select this ${this.providedFormName(command.capability)} form before changing its layout. Its settings are preserved.`);
        const reason = this.commandAuthoringReason(command);
        if (reason) throw new Error(reason);
      }
      return enableFormLayout(this.document, this.node.id, command, installed, this.catalog);
    });
  }
  setCaptureLookupSource(command: StepCommand, request: { fieldId: string; source: WorkflowLookupSource | null }): void {
    if (!this.active || this.overview || this.scope !== 'work' || this.formActionSettings || this.formLibraryOpen || this.actionLibraryOpen || this.reviewLibraryOpen || this.ownerViews.focusSuspended()
      || !this.adoptCurrentDocument() || !this.applyPendingChanges()) return;
    try {
      command = this.exactCurrentCommand(command);
      const installed = this.exactActionContract(command);
      if (!installed) throw new Error('The exact installed action is unavailable. Its saved form is preserved.');
      const baseline = JSON.stringify(this.document), next = setCaptureLookupSource(this.document, this.node.id, command, installed, request.fieldId, request.source);
      if (next !== this.document) this.edit(() => {
        if (JSON.stringify(this.document) !== baseline) throw new Error('The draft changed before the source conversion. Reopen the current form before trying again.');
        return next;
      });
    } catch (error) { this.error = error instanceof Error ? error.message : 'The choice source could not be changed.'; }
  }
  setProvidedLookupSource(request: { command: StepCommand; fieldName: string; source: WorkflowLookupSource | null; acceptFormatUpgrade: boolean }): void {
    if (!this.active || this.overview || this.scope !== 'work' || this.formActionSettings || this.formLibraryOpen || this.actionLibraryOpen || this.reviewLibraryOpen || this.ownerViews.focusSuspended()
      || !this.adoptCurrentDocument() || !this.applyPendingChanges()) return;
    try {
      const command = this.exactCurrentCommand(request.command);
      const installed = this.exactActionContract(command);
      if (!installed) throw new Error('The exact installed action is unavailable. Its saved form is preserved.');
      if (this.isRestrictedProvidedLookup(command.capability)) {
        if (this.selection.kind !== 'form' || this.selection.commandId !== commandIdentity(command))
          throw new Error(`Select this ${this.providedFormName(command.capability)} form before changing its choice source. Its settings are preserved.`);
        const reason = this.commandAuthoringReason(command);
        if (reason) throw new Error(reason);
      }
      if (providedLookupNeedsFormatUpgrade(this.document, command) && request.acceptFormatUpgrade !== true)
        throw new Error('Confirm the form format upgrade before enabling lookup choices. Its saved settings are preserved.');
      const baseline = JSON.stringify(this.document);
      const next = setProvidedLookupSource(this.document, this.node.id, command, installed, request.fieldName, request.source, this.catalog);
      if (next !== this.document) this.edit(() => {
        if (JSON.stringify(this.document) !== baseline) throw new Error('The draft changed before the source conversion. Reopen the current form before trying again.');
        return next;
      });
    } catch (error) { this.error = error instanceof Error ? error.message : 'The choice source could not be changed.'; }
  }
  setCaptureForm(command: StepCommand, captureForm: WorkflowCaptureForm): void {
    if (this.exactActionContract(command)?.supportsCaptureForm !== true) return;
    this.edit(() => this.changeCommand(command, { captureForm }));
  }
  setFormConfiguration(
    command: StepCommand,
    formConfiguration: WorkflowFormConfiguration | null,
  ): void {
    const unavailable = this.commandAuthoringReason(command);
    if (unavailable) { this.error = unavailable; return; }
    if (!this.exactActionContract(command)) return;
    if ((this.isRestrictedProvidedLookup(command.capability) || command.formConfiguration?.version === 3 || formConfiguration?.version === 3) &&
      (!this.active || this.overview || this.scope !== 'work' || this.isRestrictedProvidedLookup(command.capability) && (this.selection.kind !== 'form' || this.selection.commandId !== commandIdentity(command))
        || this.formActionSettings || this.formLibraryOpen || this.actionLibraryOpen || this.reviewLibraryOpen || this.ownerViews.focusSuspended())) return;
    this.edit(() => {
      const current = this.exactCurrentCommand(command);
      const installed = this.exactActionContract(current)!;
      if (this.isRestrictedProvidedLookup(current.capability)) {
        const context = { ownerKey: this.currentFormEditContext(), document: this.document, nodeId: this.node.id, command: current, installed, catalog: this.catalog };
        const reason = (isReviewRequestLookupCommand(current.capability)
          ? reviewRequestLookupSettingsAuthoringReason(context, current, installed)
          : preparationLookupSettingsAuthoringReason(context, current, installed))
          || providedFormSettingsShapeReason(this.document, { ...current, formConfiguration }, installed);
        if (reason) throw new Error(reason);
        if (formConfiguration && formConfiguration.version !== (current.formConfiguration?.version ?? 1))
          throw new Error('Use the form layout or choice-source controls to change this form format. Its saved settings are preserved.');
      }
      if (current.formConfiguration?.version === 3 || formConfiguration?.version === 3) {
        const reason = providedFormSettingsAuthoringReason({ ownerKey: this.currentFormEditContext(), document: this.document,
          nodeId: this.node.id, command: current, installed, catalog: this.catalog }, current, installed);
        if (reason) throw new Error(reason);
        if (formConfiguration && (formConfiguration.version !== 3 || providedLookupAuthoringReason(this.document,
          { ...current, formConfiguration }, installed)))
          throw new Error('Restore the supported form settings before applying this change. Its saved settings are preserved.');
        // Binding changes use the saved-owner chooser and its explicit format consent.
        // Removing a whole field override is the separate Restore defaults gesture.
        for (const field of formConfiguration?.fields ?? []) {
          const previous = current.formConfiguration!.fields.find(item => item.name === field.name);
          if (JSON.stringify(field.lookup ?? null) !== JSON.stringify(previous?.lookup ?? null))
            throw new Error('Use the choice-source controls before changing a saved lookup binding.');
        }
      }
      return this.changeCommand(current, { formConfiguration });
    });
  }
  updateOutcome(
    command: StepCommand,
    code: string,
    change: Partial<StepCommand['outcomes'][number]>,
  ): void {
    if (this.disabled || this.loading) return;
    const buffer = this.bufferOutcomes(command);
    if (!buffer) return;
    buffer.command.outcomes = buffer.command.outcomes.map((outcome) =>
      outcome.code === code ? { ...outcome, ...change } : outcome,
    );
    this.routeError = '';
    this.pendingChangesChange.emit(true);
  }
  private selectedRouteGestureCurrent(command: StepCommand, code: string): boolean {
    if (this.restoringRouteModel || !this.active || this.overview || this.scope !== 'behavior' || this.disabled || this.loading ||
      this.ownerViews.focusSuspended() || this.selection.kind !== 'outcomes' || this.selection.commandId !== commandIdentity(command) ||
      this.selection.outcomeCode !== code || !this.selectedOutcome(command) || !this.navigationIsCurrent() || !this.adoptCurrentDocument()) return false;
    try { this.exactCurrentRouteCommand(command, this.document); return this.outcomeIdentityAvailable(command, code) && !this.routeInspectionIssue(command); }
    catch { return false; }
  }
  private currentOutcome(command: StepCommand, code: string): StepCommand['outcomes'][number] | undefined {
    const commands = this.commands.filter(item => commandIdentity(item) === commandIdentity(command) && sameCapabilityReference(item.capability, command.capability));
    const outcomes = commands.length === 1 ? this.outcomes(commands[0]).filter(outcome => outcome.code === code) : [];
    return outcomes.length === 1 ? outcomes[0] : command.outcomes.find(outcome => outcome.code === code);
  }
  private restoreRouteModel(model: NgModel, value: unknown): void {
    if (model.control.value === value && model.viewModel === value) return;
    this.restoringRouteModel = true;
    try { model.control.setValue(value, { emitEvent: false }); }
    finally { this.restoringRouteModel = false; }
  }
  updateSelectedOutcome(command: StepCommand, code: string, property: 'label' | 'destinationNodeId', value: string, model: NgModel): void {
    if (this.restoringRouteModel) return;
    if (this.selectedRouteGestureCurrent(command, code)) {
      this.updateOutcome(command, code, { [property]: value });
      if (property === 'destinationNodeId') this.commitRoutingChange();
    }
    const canonical = this.currentOutcome(command, code)?.[property] ?? '';
    if (canonical !== value) this.restoreRouteModel(model, canonical);
  }
  changeSelectedReturn(command: StepCommand, code: string, movement: string, model: NgModel): void {
    if (this.restoringRouteModel) return;
    if (this.selectedRouteGestureCurrent(command, code)) this.changeReturn(command, code, movement === 'return');
    const canonical = this.outcomeReturns(command).has(code) ? 'return' : 'forward';
    if (canonical !== movement) this.restoreRouteModel(model, canonical);
  }
  changeSelectedOutcomeStatus(command: StepCommand, code: string, value: string, model: NgModel): void {
    if (this.restoringRouteModel) return;
    if (this.selectedRouteGestureCurrent(command, code)) this.changeOutcomeWorkflowStatus(command, code, value);
    const current = this.commands.find(item => commandIdentity(item) === commandIdentity(command)) ?? command;
    const canonical = this.outcomeWorkflowStatus(current, code);
    if (canonical !== value) this.restoreRouteModel(model, canonical);
  }
  changeSelectedReasonPolicy(command: StepCommand, code: string, value: 'optional' | 'required', model: NgModel): void {
    if (this.restoringRouteModel) return;
    const contract = this.contract(command);
    if (this.selectedRouteGestureCurrent(command, code) && contract && contract.completesStep !== false && this.reasonPolicies(contract, code).includes(value))
      this.changeOutcome(command, code, { reasonPolicy: value });
    const canonical = this.currentOutcome(command, code)?.reasonPolicy ?? '';
    if (canonical !== value) this.restoreRouteModel(model, canonical);
  }
  returnSelectedToPrevious(command: StepCommand, code: string): void {
    if (this.selectedRouteGestureCurrent(command, code)) this.returnToPreviousAndCommit(command, code);
  }
  changeOutcome(
    command: StepCommand,
    code: string,
    change: Partial<StepCommand['outcomes'][number]>,
  ): void {
    this.updateOutcome(command, code, change);
    this.commitRoutingChange();
  }
  changeOutcomeWorkflowStatus(
    command: StepCommand,
    code: string,
    statusCode: string,
  ): void {
    this.setOutcomeWorkflowStatus(command, code, statusCode);
    this.commitRoutingChange();
  }
  changeReturn(command: StepCommand, code: string, checked: boolean): void {
    this.setReturn(command, code, checked);
    this.commitRoutingChange();
  }
  returnToPreviousAndCommit(command: StepCommand, code: string): void {
    this.returnToPrevious(command, code);
    this.commitRoutingChange();
  }
  /** Labels remain buffered while typing; the UI calls this on blur or Enter. */
  commitRoutingChange(): void {
    const buffer = this.routeBuffer;
    if (
      !this.immediateRoutingChanges ||
      !buffer?.immediateEligible ||
      buffer.setupDocument ||
      buffer.command.outcomes.some(
        (outcome) => !outcome.label.trim() || !outcome.destinationNodeId,
      )
    )
      return;
    this.applyPendingChanges();
  }
  setRecordedReasonPolicy(
    command: StepCommand,
    code: string,
    reasonPolicy: 'optional' | 'required',
  ): void {
    if (!this.adoptCurrentDocument()) return;
    try { command = this.exactCurrentCommand(command); } catch (error) { this.error = error instanceof Error ? error.message : 'Select this action again.'; return; }
    const contract = this.exactActionContract(command);
    if (
      contract?.completesStep !== false ||
      !contract.outcomes
        .find((o) => o.code === code)
        ?.reasonPolicies.includes(reasonPolicy as 'optional' | 'required')
    )
      return;
    if (!this.applyPendingChanges()) return;
    command = this.commands.find(item => sameCapabilityReference(item.capability, command.capability))!;
    this.edit(() =>
      this.changeCommand(command, {
        outcomes: command.outcomes.map((o) =>
          o.code === code ? { ...o, reasonPolicy } : o,
        ),
      }),
    );
  }
  recordedReasonPolicies(
    command: StepCommand,
    code: string,
  ): ('optional' | 'required')[] {
    return (
      this.exactActionContract(command)?.outcomes.find((o) => o.code === code)
        ?.reasonPolicies ?? []
    );
  }
  moduleCheckExplanation(command: StepCommand, code: string): string {
    if (command.capability.key.provider === 'workflow' && command.capability.key.code === 'workflow.review.record' &&
      command.capability.key.version === 1 && code === 'review.changes-reason') {
      const result = command.outcomes.find(outcome => outcome.code === 'changes_required');
      return result ? `Applies when ${result.label} is recorded.` : '';
    }
    return '';
  }
  returnCodes(command: StepCommand): Set<string> {
    const route = this.document.edges.find(
      (edge) => edge.fromNodeId === this.node.id,
    )?.toNodeId;
    return new Set(
      this.document.edges
        .filter(
          (edge) =>
            edge.fromNodeId === route &&
            edge.pathKindCode === 'return' &&
            command.outcomes.some(
              (outcome) => outcome.code === edge.outcomeCode,
            ),
        )
        .map((edge) => edge.outcomeCode!),
    );
  }
  setReturn(command: StepCommand, code: string, checked: boolean): void {
    if (this.disabled || this.loading) return;
    const buffer = this.bufferOutcomes(command);
    if (!buffer) return;
    if (checked) buffer.returns.add(code);
    else buffer.returns.delete(code);
    const eligible = this.outcomeDestinations(command, code);
    buffer.command.outcomes = buffer.command.outcomes.map((outcome) =>
      outcome.code === code &&
      outcome.destinationNodeId &&
      !eligible.some((target) => target.id === outcome.destinationNodeId)
        ? { ...outcome, destinationNodeId: '' }
        : outcome,
    );
    this.routeError = '';
    this.pendingChangesChange.emit(true);
  }
  reasonPolicies(contract: StepAuthoringCommand, code: string) {
    return (
      contract.outcomes.find((item) => item.code === code)?.reasonPolicies ?? []
    );
  }
  setNext(destination: string, select?: HTMLSelectElement): void {
    if (this.applyPendingChanges() && destination)
      this.edit(() =>
        setStepNextPath(this.document, this.node.id, destination),
      );
    // A rejected route must not leave the native control showing a value that was never applied.
    if (select) select.value = this.nextDestination;
  }
  setSelectedNext(destination: string, select: HTMLSelectElement, model: NgModel): void {
    if (this.restoringRouteModel) return;
    const latest = this.currentDocument();
    if (this.active && !this.overview && this.scope === 'behavior' && this.selection.kind === 'next' && !this.ownerViews.focusSuspended() &&
      this.ownerIsCurrent() && this.navigationIsCurrent() && latest && this.routingFingerprint(latest) === this.routingFingerprint()) this.setNext(destination, select);
    this.restoreRouteModel(model, this.nextDestination);
  }
  interactions(command: StepCommand) {
    return (
      this.catalog?.interactions.filter((item) =>
        sameCapabilityReference(item.command, command.capability),
      ) ?? []
    );
  }
  useForm(command: StepCommand): void {
    const interaction = this.interactions(command)[0];
    if (!interaction) return;
    this.edit(() => {
      const declaration = this.node.declaration!;
      return replacePresentation(this.document, this.node.id, {
        ...declaration,
        detail: {
          ...declaration.detail,
          mainInteraction: {
            code: interaction.code,
            version: interaction.version,
            command: interaction.command,
          },
        },
      });
    });
  }
  isMainForm(command: StepCommand) {
    const current = this.node.declaration?.detail.mainInteraction;
    return (
      !!current && sameCapabilityReference(current.command, command.capability)
    );
  }
  addView(): void {
    const view = this.catalog?.views.find(
      (item) => capabilityIdentity(item.capability.key) === this.selectedView,
    );
    if (view)
      this.edit(() =>
        addStepView(this.document, this.node.id, view, this.capabilities),
      );
    if (!this.error) this.selectedView = '';
  }
  /** Preview only; the presentation owner supplies the explicit Add receipt. */
  dashboardSourceReason(reference: StepCommand['capability'], current = this.currentDocument()): string | null {
    try { this.proposeDashboardSource(reference, current); return null; }
    catch (error) { return error instanceof Error ? error.message : 'The selected dashboard source is unavailable.'; }
  }
  proposeDashboardSource(reference: StepCommand['capability'], current = this.currentDocument()): WorkflowDefinitionDocument {
    if (!current || this.destroyed || !this.active || this.scope !== 'dashboard' || this.disabled || this.loading || this.catalogError
      || !this.ownerIsCurrent() || this.documentContext(current) !== this.inputContext
      || this.latestDocument && JSON.stringify(current) !== JSON.stringify(this.currentDocument()))
      throw new Error('This dashboard owner or installed source changed. Review the current step before adding a dashboard.');
    if (this.hasPendingChanges()) throw new Error('Apply or discard the current component input before adding a dashboard.');
    if (!this.catalog) throw new Error('Installed dashboard sources have not been loaded.');
    return addStepDashboard(current, this.node.id, reference, this.catalog, this.capabilities);
  }
  viewIdentity(view: { capability: StepAuthoringCommand['capability'] }) {
    return capabilityIdentity(view.capability.key);
  }
}
